import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { 
  UserProfile, 
  FinancialConfig, 
  Quote, 
  Material, 
  QuoteProductItem, 
  ProductTemplate, 
  SimpleService, 
  StatusHistoryEntry, 
  AppView,
  Client,
  Supplier,
  WorkOrder,
  DigitalSignature,
  Receipt,
  Appointment,
  AppointmentStatus,
  StaffMember,
  SubscriptionClubPlan,
  StorefrontProfile,
  InventoryBatch,
  StockMovement,
  ClinicalPatient,
  ClinicalConsultation,
  ClinicalVaccination,
  ClinicalExamVault,
  AccountantAccess
} from '../types';
import { loadState, saveState } from '../store';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../utils/telemetry';
import { safeStorage } from '../utils/safeStorage';
import { useServiceStore } from '../store/useServiceStore';
import { isMasterEmail } from '../constants';
import { extractDominantColor } from '../utils/colorExtractor';
import {
  mapSupabaseMaterial,
  mapSupabaseQuote,
  mapSupabaseService,
  mapSupabaseRecipe,
  mapSupabaseClient,
  mapSupabaseSupplier,
  mapSupabaseWorkOrder,
  mapSupabaseReceipt,
  mapSupabaseAppointment,
  mapSupabaseStaffMember,
  mapSupabaseSubscriptionClub,
  mapSupabaseBatch,
  mapSupabaseStockMovement,
  mapSupabasePatient,
  mapSupabaseConsultation,
  mapSupabaseVaccination,
  mapSupabaseExam,
  mapSupabaseAccountantAccess
} from './sync/syncMappers';
import { splitSyncQueueByUser } from './sync/syncQueueUtils';

import type {
  SaveQuoteInput,
  SyncQueueItem,
  UseAppDataSyncParams,
  UseAppDataSyncReturn
} from './sync/syncTypes';

export type {
  SaveQuoteInput,
  SyncQueueItem,
  UseAppDataSyncParams,
  UseAppDataSyncReturn
} from './sync/syncTypes';

export const resolveDefaultViewForUser = (targetPlan?: string, isMaster?: boolean, isTrial?: boolean): AppView => {
  const isEnterprise = Boolean(isMaster || ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(targetPlan || ''));
  const isSpecialist = Boolean(targetPlan === 'Especialista' || targetPlan === 'Premium' || isTrial);
  const isPro = Boolean(targetPlan === 'Pro');
  if (isEnterprise || isSpecialist) return 'erp-hub';
  if (isPro) return 'home';
  return 'home';
};

const getGuestUser = (): UserProfile => {
  let initialPlan: 'Grátis' | 'Pro' | 'Especialista' | 'Empresa' = 'Grátis';
  let initialNiche = 'Geral';
  let initialBusinessName = '';
  let initialOwner = 'Operador';

  try {
    const isAllowedToSimulate = Boolean(import.meta.env.DEV);
    const params = new URLSearchParams(window.location.search);
    const p = params.get('plan')?.toLowerCase();
    const isDemo = isAllowedToSimulate && params.get('demo') === 'true';
    const vert = params.get('vertical');

    if (isAllowedToSimulate && (p === 'empresa' || p === 'enterprise' || p === 'empresarial' || isDemo)) {
      initialPlan = 'Empresa';
    } else if (isAllowedToSimulate && (p === 'especialista' || p === 'specialist' || p === 'premium')) {
      initialPlan = 'Especialista';
    } else if (isAllowedToSimulate && p === 'pro') {
      initialPlan = 'Pro';
    } else {
      initialPlan = 'Grátis';
    }

    if (vert === 'retail_bulk' || window.location.pathname.includes('granel') || window.location.pathname.includes('emporio')) {
      initialNiche = 'Varejo e Produtos a Granel';
      initialBusinessName = '';
      initialOwner = 'Operador';
    }
  } catch (_) {}

  return {
    id: 'guest',
    ownerName: initialOwner,
    email: '',
    businessName: initialBusinessName,
    niche: initialNiche,
    plan: initialPlan,
    actingType: 'Novo na área'
  };
};

const GUEST_USER: UserProfile = getGuestUser();

const GUEST_CONFIG: FinancialConfig = {
  userId: 'guest',
  proLaboreGoal: 0,
  monthlyFixedCost: 0,
  weeklyWorkHours: 40,
  baseHourValue: 0,
  paymentFeePercentage: 0,
  defaultCommissionPercentage: 0
};

export function useAppDataSync({
  session,
  setView
}: UseAppDataSyncParams): UseAppDataSyncReturn {
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [config, setConfig] = useState<FinancialConfig | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [recipes, setRecipes] = useState<ProductTemplate[]>([]);
  const [services, setServices] = useState<SimpleService[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [subscriptionClubs, setSubscriptionClubs] = useState<SubscriptionClubPlan[]>([]);
  const [storefrontProfile, setStorefrontProfile] = useState<StorefrontProfile | null>(null);
  const [inventoryBatches, setInventoryBatches] = useState<InventoryBatch[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [clinicalPatients, setClinicalPatients] = useState<ClinicalPatient[]>([]);
  const [clinicalConsultations, setClinicalConsultations] = useState<ClinicalConsultation[]>([]);
  const [clinicalVaccinations, setClinicalVaccinations] = useState<ClinicalVaccination[]>([]);
  const [clinicalExams, setClinicalExams] = useState<ClinicalExamVault[]>([]);
  const [accountantAccesses, setAccountantAccesses] = useState<AccountantAccess[]>([]);
  const [hasLoadedAllData, setHasLoadedAllData] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  const lastLoadedId = React.useRef<string | null>(null);
  const activeUserIdRef = React.useRef<string | null>(null);

  // ATENÇÃO / WARNING:
  // Os refs abaixo são cruciais para manter a função `loadUserData` com dependências vazias `[]`.
  // Atualizar estados como `user`, `isLoadingUserData` ou `hasLoadedAllData` diretamente dentro de
  // `loadUserData` faria com que o callback fosse recriado a cada render, disparando o useEffect
  // de carregamento repetidamente e resultando em um loop infinito de requisições de rede (Supabase).
  // NÃO REMOVA OU SUBSTITUA por dependências diretas.
  const sessionRef = React.useRef(session);
  const setViewRef = React.useRef(setView);
  const userRef = React.useRef(user);
  const hasLoadedAllDataRef = React.useRef(hasLoadedAllData);
  const materialsRef = React.useRef(materials);
  const quotesRef = React.useRef(quotes);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    setViewRef.current = setView;
  }, [setView]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    hasLoadedAllDataRef.current = hasLoadedAllData;
  }, [hasLoadedAllData]);

  useEffect(() => {
    materialsRef.current = materials;
  }, [materials]);

  useEffect(() => {
    quotesRef.current = quotes;
  }, [quotes]);

  // Centralized Reactive Persistence
  useEffect(() => {
    if (user && config && hasLoadedAllData && user.id !== 'guest') {
      saveState({ 
        user, 
        financialConfig: config, 
        quotes, 
        materials, 
        recipes, 
        services,
        clients,
        suppliers,
        workOrders,
        receipts,
        appointments,
        staffMembers,
        subscriptionClubs,
        storefrontProfile,
        inventoryBatches,
        stockMovements,
        clinicalPatients,
        clinicalConsultations,
        clinicalVaccinations,
        clinicalExams
      });
    }
  }, [
    user, 
    config, 
    quotes, 
    materials, 
    recipes, 
    services, 
    clients, 
    suppliers, 
    workOrders, 
    receipts, 
    appointments, 
    staffMembers, 
    subscriptionClubs, 
    storefrontProfile,
    inventoryBatches,
    stockMovements,
    clinicalPatients,
    clinicalConsultations,
    clinicalVaccinations,
    clinicalExams,
    hasLoadedAllData
  ]);

  const handleAuthError = React.useCallback(async (err: any): Promise<boolean> => {
    if (!err) return false;
    const status = err.status || err.statusCode;
    const msg = err.message || String(err);
    if (status === 401 || msg.includes('JWT') || msg.includes('Unauthorized')) {
      try {
        await supabase.auth.signOut();
      } catch (signOutErr) {
        console.error('Error signing out during auth error handling:', signOutErr);
      }
      return true;
    }
    return false;
  }, []);

  const loadUserData = React.useCallback(async (userId: string, isCancelled?: () => boolean, forceReload = false) => {
    if (!forceReload && lastLoadedId.current === userId && hasLoadedAllDataRef.current) return;
    
    if (forceReload) {
      lastLoadedId.current = null;
    }
    
    activeUserIdRef.current = userId;

    // 0. ESTRATÉGIA SWR (Stale-While-Revalidate): Carrega imediatamente do cache local para render instantâneo (0ms)
    const localCachedState = loadState();
    const hasValidLocalCache = Boolean(localCachedState.user && localCachedState.user.id === userId);

    if (hasValidLocalCache) {
      if (localCachedState.user) setUser(localCachedState.user);
      if (localCachedState.financialConfig) setConfig(localCachedState.financialConfig);
      if (localCachedState.quotes?.length) setQuotes(localCachedState.quotes);
      if (localCachedState.materials?.length) setMaterials(localCachedState.materials);
      if (localCachedState.recipes?.length) setRecipes(localCachedState.recipes);
      if (localCachedState.services?.length) setServices(localCachedState.services);
      if (localCachedState.clients?.length) setClients(localCachedState.clients);
      if (localCachedState.suppliers?.length) setSuppliers(localCachedState.suppliers);
      if (localCachedState.workOrders?.length) setWorkOrders(localCachedState.workOrders);
      if (localCachedState.receipts?.length) setReceipts(localCachedState.receipts);
      if (localCachedState.appointments?.length) setAppointments(localCachedState.appointments);
      if (localCachedState.staffMembers?.length) setStaffMembers(localCachedState.staffMembers);
      if (localCachedState.storefrontProfile) setStorefrontProfile(localCachedState.storefrontProfile);
      
      // Interface liberada imediatamente sem bloquear o usuário
      setIsLoadingUserData(false);
      setHasLoadedAllData(true);
    } else {
      // Primeiro acesso nesta máquina (sem cache prévio): exibe loading inicial
      setIsLoadingUserData(true);
      setHasLoadedAllData(false);
      setUser(null);
      setConfig(null);
      setQuotes([]);
      setMaterials([]);
      setRecipes([]);
      setServices([]);
    }

    try {
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (isCancelled && isCancelled()) return;
      if (activeUserIdRef.current !== userId) return;

      // 1. Identificar se o usuário atual é colaborador de um Workspace de equipe ativo
      let effectiveWorkspaceId = userId;
      let isCollaborator = false;

      const { data: teamMemberRow } = await supabase
        .from('workspace_members')
        .select('workspace_owner_id, status')
        .eq('member_user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (teamMemberRow?.workspace_owner_id) {
        effectiveWorkspaceId = teamMemberRow.workspace_owner_id;
        isCollaborator = true;
      }

      const { data: subData } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', effectiveWorkspaceId)
        .maybeSingle();
      if (isCancelled && isCancelled()) return;
      if (activeUserIdRef.current !== userId) return;

      const userEmail = (userData?.email || session?.user?.email || '').toLowerCase().trim();
      const isMaster = isMasterEmail(userEmail);
      
      const isPaidActive = isCollaborator || Boolean(
        subData && 
        (subData.status === 'active' || subData.status === 'past_due') && 
        (!subData.access_ends_at || new Date(subData.access_ends_at) > new Date())
      );

      // Verificação do status de Trial
      let trialEnd = subData?.trial_ends_at || (subData?.status === 'trialing' ? subData?.access_ends_at : null);
      const isTrialExpired = Boolean(
        subData &&
        subData.trial_used === true &&
        subData.status !== 'trialing' &&
        subData.status !== 'active' &&
        subData.status !== 'past_due' &&
        trialEnd &&
        new Date(trialEnd) <= new Date()
      );

      let isTrialActive = false;
      let effectiveTrialStartsAt = subData?.trial_starts_at || null;
      let effectiveTrialEndsAt = trialEnd || null;

      if (isMaster || isCollaborator) {
        isTrialActive = false;
      } else if (isPaidActive) {
        isTrialActive = false;
      } else if (isTrialExpired) {
        isTrialActive = false;
      } else {
        // Se estiver ativamente em trialing (ou em pending_payment mas dentro do prazo do trial):
        if ((subData?.status === 'trialing' || subData?.status === 'pending_payment') && trialEnd && new Date(trialEnd) > new Date()) {
          isTrialActive = true;
          effectiveTrialEndsAt = trialEnd;
        } else if (!subData || subData.trial_used !== true || subData.status === 'paused' || !trialEnd) {
          // Auto-ativação imediata (14 dias ou 30 dias se houver cupom salvo)
          const savedCoupon = safeStorage.getItem('rmax_referred_coupon') || null;
          const trialDaysToGrant = savedCoupon ? 30 : 14;
          isTrialActive = true;
          const now = new Date();
          effectiveTrialStartsAt = now.toISOString();
          effectiveTrialEndsAt = new Date(now.getTime() + trialDaysToGrant * 24 * 60 * 60 * 1000).toISOString();
          
          // Persistir ativação no Supabase em segundo plano
          supabase.rpc('activate_user_trial', { p_coupon: savedCoupon }).then(
            ({ error }) => {
              if (error) {
                console.warn('[useAppDataSync] Auto-ativação de trial via RPC em segundo plano (aviso):', error);
              }
            },
            (err) => {
              console.warn('[useAppDataSync] Auto-ativação de trial via RPC em segundo plano:', err);
            }
          );
        }
      }

      const isPro = isMaster || isCollaborator || isTrialActive || isPaidActive;
      
      if (userData) {
        const isEnterprisePaid = isPaidActive && (
          subData?.plan_type === 'empresa' || 
          subData?.plan_type === 'enterprise' || 
          subData?.plan_type === 'empresarial'
        );
        const isSpecialistPaid = isPaidActive && (subData?.plan_type === 'especialista' || subData?.plan_type === 'especialista_anual');
        const isEnterpriseSelected = (userData.plan && ['Empresa', 'Empresarial', 'Enterprise'].includes(userData.plan)) || subData?.plan_type === 'empresa';
        const isProSelected = userData.plan === 'Pro' || subData?.plan_type === 'pro';
        const isSpecialistSelected = (userData.plan && ['Especialista', 'Premium'].includes(userData.plan)) || subData?.plan_type === 'especialista';

        const resolvedPlan: 'Grátis' | 'Pro' | 'Especialista' | 'Empresa' = (isMaster || isEnterprisePaid || isEnterpriseSelected)
          ? 'Empresa'
          : isCollaborator
          ? 'Especialista'
          : isSpecialistPaid || isSpecialistSelected
          ? 'Especialista'
          : isProSelected
          ? 'Pro'
          : isTrialActive
          ? (subData?.plan_type === 'empresa' ? 'Empresa' : (subData?.plan_type === 'pro' ? 'Pro' : 'Especialista'))
          : (isPro ? 'Pro' : 'Grátis');

        const mappedUser: UserProfile = {
          id: userData.id,
          effectiveWorkspaceId: effectiveWorkspaceId,
          isTeamMember: isCollaborator,
          ownerName: userData.name || '',
          email: userData.email || session?.user?.email || '',
          birthDate: userData.birth_date || '',
          taxRegime: userData.tax_regime || '',
          businessName: userData.business_name || '',
          niche: userData.niche || '',
          profession: userData.profession || '',
          actingType: userData.journey_status || 'Novo na área',
          city: userData.city || '',
          state: userData.state || '',
          zipCode: userData.zip_code || '',
          businessAddress: (userData.business_address?.split('###') || [])[0] || '',
          businessNumber: (userData.business_address?.split('###') || [])[1] || '',
          logoUrl: userData.logo_url || '',
          pixKey: userData.pix_key || '',
          pixKeyType: userData.pix_key_type || '',
          lgpdConsent: userData.lgpd_consent || null,
          plan: resolvedPlan,
          planType: isMaster ? 'empresa' : (isEnterprisePaid ? 'empresa' : (isCollaborator ? 'especialista' : (isTrialActive ? 'especialista' : (subData?.plan_type || null)))),
          subscriptionStatus: (isMaster || isCollaborator) ? 'active' : (isPaidActive ? 'active' : (isTrialActive ? 'trialing' : (subData?.status || 'paused'))),
          accessEndsAt: (isMaster || isCollaborator) ? '2099-12-31T23:59:59Z' : (isPaidActive ? (subData?.access_ends_at || null) : (isTrialActive ? effectiveTrialEndsAt : (subData?.access_ends_at || null))),
          trialStartsAt: effectiveTrialStartsAt,
          trialEndsAt: effectiveTrialEndsAt,
          isTrialActive: isTrialActive,
          mpPreapprovalId: subData?.mp_preapproval_id || null,
          mpPreferenceId: subData?.mp_preference_id || null,
          cancelReason: subData?.cancel_reason || null,
          maxFreeQuotes: isMaster || isCollaborator || isTrialActive ? 999999 : (userData.max_free_quotes || 5),
          trialUsed: isTrialExpired ? true : (isTrialActive ? true : (subData ? (subData.trial_used ?? false) : false)),
          isMasterUser: isMaster,
          isAdmin: isMaster || Boolean(userData.is_admin),
          marketDataSharingConsent: Boolean(userData.market_data_sharing_consent),
          marketDataConsentedAt: userData.market_data_consented_at || null,
          marketDataConsentRevokedAt: userData.market_data_consent_revoked_at || null
        };
        if (isCancelled && isCancelled()) return;
        setUser(mappedUser);

        // Identificação de privilégios de plano para carregamento escalonado (redução drástica de carga no banco)
        const isSpecialistOrAbove = isMaster || isCollaborator || isTrialActive || resolvedPlan === 'Especialista' || resolvedPlan === 'Empresa';
        const isEnterpriseOrAbove = isMaster || isCollaborator || isTrialActive || resolvedPlan === 'Empresa';

        // 1. CARREGAMENTO IMEDIATO DO CORE (Apenas 5 consultas essenciais com limites de proteção)
        const [
          configRes, 
          materialsRes, 
          quotesRes, 
          servicesRes, 
          recipesRes
        ] = await Promise.all([
          supabase.from('user_business_profiles').select('*').eq('user_id', effectiveWorkspaceId).maybeSingle(),
          supabase.from('materials').select('*').eq('user_id', effectiveWorkspaceId).limit(300),
          supabase.from('quotes').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(100),
          supabase.from('simple_services').select('*').eq('user_id', effectiveWorkspaceId).limit(200),
          supabase.from('product_templates').select('*, template_items(*)').eq('user_id', effectiveWorkspaceId).limit(100)
        ]);

        if (isCancelled && isCancelled()) return;
        if (activeUserIdRef.current !== userId) return;

        // 2. CARREGAMENTO ESCALONADO DO ERP ESPECIALISTA (Apenas se o plano contemplar)
        let clientsData: any[] | null = null;
        let suppliersData: any[] | null = null;
        let workOrdersData: any[] | null = null;
        let receiptsData: any[] | null = null;
        let appointmentsData: any[] | null = null;
        let staffData: any[] | null = null;
        let clubsData: any[] | null = null;
        let storefrontData: any = null;

        if (isSpecialistOrAbove) {
          try {
            const [
              clientsRes,
              suppliersRes,
              workOrdersRes,
              receiptsRes,
              appointmentsRes,
              staffRes,
              clubsRes,
              storefrontRes
            ] = await Promise.all([
              supabase.from('clients').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(150),
              supabase.from('suppliers').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(100),
              supabase.from('work_orders').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(100),
              supabase.from('receipts').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(150),
              supabase.from('appointments').select('*').eq('user_id', effectiveWorkspaceId).order('scheduled_date', { ascending: false }).limit(150),
              supabase.from('staff_members').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: true }),
              supabase.from('subscription_clubs').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: true }),
              supabase.from('storefront_profiles').select('*').eq('user_id', effectiveWorkspaceId).maybeSingle()
            ]);

            clientsData = clientsRes?.data;
            suppliersData = suppliersRes?.data;
            workOrdersData = workOrdersRes?.data;
            receiptsData = receiptsRes?.data;
            appointmentsData = appointmentsRes?.data;
            staffData = staffRes?.data;
            clubsData = clubsRes?.data;
            storefrontData = storefrontRes?.data;
          } catch (erpErr) {
            console.warn('[useAppDataSync] Falha não impeditiva no sync ERP Especialista:', erpErr);
          }
        }

        // 3. CARREGAMENTO ESCALONADO DO ERP EMPRESA (Apenas se for Enterprise/Master)
        let batchesData: any[] | null = null;
        let movementsData: any[] | null = null;
        let patientsData: any[] | null = null;
        let consultationsData: any[] | null = null;
        let vaccinationsData: any[] | null = null;
        let examsData: any[] | null = null;
        let accountantsData: any[] | null = null;

        if (isEnterpriseOrAbove) {
          try {
            const [
              batchesRes,
              movementsRes,
              patientsRes,
              consultationsRes,
              vaccinationsRes,
              examsRes,
              accountantsRes
            ] = await Promise.all([
              supabase.from('inventory_batches').select('*').eq('user_id', effectiveWorkspaceId).order('expiration_date', { ascending: true }).limit(100),
              supabase.from('stock_movements').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(50),
              supabase.from('clinical_patients').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(100),
              supabase.from('clinical_consultations').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(50),
              supabase.from('clinical_vaccinations').select('*').eq('user_id', effectiveWorkspaceId).order('next_due_date', { ascending: true }).limit(100),
              supabase.from('clinical_exams_vault').select('*').eq('user_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(50),
              supabase.from('accountant_access').select('*').eq('workspace_owner_id', effectiveWorkspaceId).order('created_at', { ascending: false }).limit(50)
            ]);

            batchesData = batchesRes?.data;
            movementsData = movementsRes?.data;
            patientsData = patientsRes?.data;
            consultationsData = consultationsRes?.data;
            vaccinationsData = vaccinationsRes?.data;
            examsData = examsRes?.data;
            accountantsData = accountantsRes?.data;
          } catch (entErr) {
            console.warn('[useAppDataSync] Falha não impeditiva no sync ERP Empresa:', entErr);
          }
        }

        const configData = configRes.data;
        const materialsData = materialsRes.data;
        const quotesData = quotesRes.data;
        const servicesData = servicesRes.data;
        const recipesData = recipesRes.data;
        
        if (configData) {
          if (isCancelled && isCancelled()) return;
          setConfig({
            userId: configData.user_id,
            proLaboreGoal: configData.pro_labore_goal || 0,
            monthlyFixedCost: configData.monthly_fixed_costs || 0,
            weeklyWorkHours: configData.weekly_work_hours || 44,
            baseHourValue: configData.base_hour_value || 0,
            paymentFeePercentage: configData.payment_fee_percentage || 0,
            defaultCommissionPercentage: configData.default_commission_percentage || 0,
            logoUrl: configData.logo_url
          });

          setUser(prev => prev ? ({
            ...prev,
            ownerName: configData.owner_name || prev.ownerName,
            businessName: configData.business_name || prev.businessName,
            businessAddress: (configData.business_address?.split('###') || [])[0] || prev.businessAddress,
            businessNumber: (configData.business_address?.split('###') || [])[1] || prev.businessNumber || '',
            zipCode: configData.zip_code || prev.zipCode,
            showAddress: configData.show_address || false,
            showPixKey: configData.show_pix_key ?? false,
            logoUrl: configData.logo_url || prev.logoUrl,
            profession: configData.profession || prev.profession,
            taxRegime: configData.company_type || prev.taxRegime,
            pixKey: configData.pix_key || '',
            pixKeyType: configData.pix_key_type || '',
            paymentFeePercentage: configData.payment_fee_percentage || 0,
            defaultCommissionPercentage: configData.default_commission_percentage || 0,
            nicheChangeCount: configData.niche_change_count ?? prev.nicheChangeCount ?? 0,
            plan: isMaster ? 'Empresa' : (prev.isTrialActive ? 'Especialista' : (prev.plan || configData.plan || 'Grátis'))
          }) : null);
        } else {
          // NOVO USUÁRIO: Perfil de negócio ainda não cadastrado no Supabase
          if (isCancelled && isCancelled()) return;
          const fallbackConfig: FinancialConfig = {
            userId: effectiveWorkspaceId,
            proLaboreGoal: 3000,
            monthlyFixedCost: 1500,
            weeklyWorkHours: 40,
            baseHourValue: 25,
            paymentFeePercentage: 0,
            defaultCommissionPercentage: 0
          };
          setConfig(fallbackConfig);
        }

        if (materialsData) {
          if (isCancelled && isCancelled()) return;
          setMaterials(materialsData.map(mapSupabaseMaterial));
        }

        if (quotesData) {
          if (isCancelled && isCancelled()) return;
          setQuotes(quotesData.map(mapSupabaseQuote));
        }

        if (servicesData) {
          if (isCancelled && isCancelled()) return;
          const mappedServices = servicesData.map(mapSupabaseService);
          setServices(mappedServices);
          useServiceStore.getState().setServices(mappedServices);
        }

        if (recipesData) {
          if (isCancelled && isCancelled()) return;
          setRecipes(recipesData.map(mapSupabaseRecipe));
        }

        if (clientsData) {
          if (isCancelled && isCancelled()) return;
          setClients(clientsData.map(mapSupabaseClient));
        }

        if (suppliersData) {
          if (isCancelled && isCancelled()) return;
          setSuppliers(suppliersData.map(mapSupabaseSupplier));
        }

        if (workOrdersData) {
          if (isCancelled && isCancelled()) return;
          setWorkOrders(workOrdersData.map(mapSupabaseWorkOrder));
        }

        if (receiptsData) {
          if (isCancelled && isCancelled()) return;
          setReceipts(receiptsData.map(mapSupabaseReceipt));
        }

        if (appointmentsData) {
          if (isCancelled && isCancelled()) return;
          setAppointments(appointmentsData.map(mapSupabaseAppointment));
        }

        if (staffData) {
          if (isCancelled && isCancelled()) return;
          setStaffMembers(staffData.map(mapSupabaseStaffMember));
        }

        if (clubsData) {
          if (isCancelled && isCancelled()) return;
          setSubscriptionClubs(clubsData.map(mapSupabaseSubscriptionClub));
        }

        if (storefrontData) {
          if (isCancelled && isCancelled()) return;
          setStorefrontProfile({
            id: storefrontData.id,
            userId: storefrontData.user_id,
            slug: storefrontData.slug,
            displayName: storefrontData.display_name,
            tagline: storefrontData.tagline || undefined,
            bio: storefrontData.bio || undefined,
            yearsExperience: storefrontData.years_experience || undefined,
            nicheBadges: Array.isArray(storefrontData.niche_badges) ? storefrontData.niche_badges : [],
            whatsappNumber: storefrontData.whatsapp_number,
            instagramHandle: storefrontData.instagram_handle || undefined,
            facebookHandle: storefrontData.facebook_handle || undefined,
            tiktokHandle: storefrontData.tiktok_handle || undefined,
            email: storefrontData.email || undefined,
            addressDisplay: storefrontData.address_display || undefined,
            accentColor: storefrontData.accent_color || '#10b981',
            logoUrl: storefrontData.logo_url || configData?.logo_url || (user as any)?.logoUrl || undefined,
            coverUrl: storefrontData.cover_url || undefined,
            businessHours: storefrontData.business_hours || safeStorage.getItem('rmax_weekly_business_hours') || 'Terça a Sábado: 08:00 às 19:00',
            isPublished: Boolean(storefrontData.is_published),
            viewCount: Number(storefrontData.view_count || 0),
            createdAt: storefrontData.created_at,
            updatedAt: storefrontData.updated_at
          });

          // Se a vitrine no banco estava sem logo mas o perfil de negócio tem, sincroniza em background
          const syncLogo = storefrontData.logo_url || configData?.logo_url || (user as any)?.logoUrl;
          if (!storefrontData.logo_url && syncLogo) {
            supabase
              .from('storefront_profiles')
              .update({ logo_url: syncLogo })
              .eq('id', storefrontData.id)
              .then(() => {});
          }
        } else {
          const bName = configData?.business_name || (user as any)?.businessName || configData?.owner_name || (user as any)?.ownerName || 'Meu Negócio';
          const rawSlug = (bName || 'empresa')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
          const autoSlug = (rawSlug && rawSlug !== 'meu-negocio' && rawSlug !== 'minha-empresa' && rawSlug !== 'empresa')
            ? `${rawSlug}-${userId.slice(0, 6)}`
            : `empresa-${userId.slice(0, 6)}`;

          const newProfile: StorefrontProfile = {
            id: 'prof_' + userId,
            userId: userId,
            slug: autoSlug,
            displayName: bName,
            tagline: configData?.profession || (user as any)?.profession || 'Atendimento Profissional',
            nicheBadges: [configData?.profession || (user as any)?.profession || 'Atendimento Especializado'],
            whatsappNumber: (configData?.business_address?.split('###') || [])[2] || (user as any)?.whatsappNumber || '',
            addressDisplay: (configData?.business_address?.split('###') || [])[0] || (user as any)?.businessAddress || '',
            accentColor: '#10b981',
            logoUrl: configData?.logo_url || (user as any)?.logoUrl || undefined,
            businessHours: safeStorage.getItem('rmax_weekly_business_hours') || 'Terça a Sábado: 08:00 às 19:00',
            isPublished: true,
            viewCount: 0,
            portfolioItems: []
          };

          setStorefrontProfile(newProfile);

          supabase.from('storefront_profiles').upsert({
            user_id: userId,
            slug: newProfile.slug,
            display_name: newProfile.displayName,
            tagline: newProfile.tagline || null,
            whatsapp_number: newProfile.whatsappNumber || '',
            address_display: newProfile.addressDisplay || null,
            accent_color: '#10b981',
            logo_url: newProfile.logoUrl || null,
            is_published: true,
            view_count: 0
          }, { onConflict: 'user_id' }).catch((err) => {
            console.warn('[storefront_profiles] Sync silencioso de perfil:', err);
          });
        }

        if (batchesData) {
          if (isCancelled && isCancelled()) return;
          setInventoryBatches(batchesData.map(mapSupabaseBatch));
        }

        if (movementsData) {
          if (isCancelled && isCancelled()) return;
          setStockMovements(movementsData.map(mapSupabaseStockMovement));
        }

        if (patientsData) {
          if (isCancelled && isCancelled()) return;
          setClinicalPatients(patientsData.map(mapSupabasePatient));
        }

        if (consultationsData) {
          if (isCancelled && isCancelled()) return;
          setClinicalConsultations(consultationsData.map(mapSupabaseConsultation));
        }

        if (vaccinationsData) {
          if (isCancelled && isCancelled()) return;
          setClinicalVaccinations(vaccinationsData.map(mapSupabaseVaccination));
        }

        if (examsData) {
          if (isCancelled && isCancelled()) return;
          setClinicalExams(examsData.map(mapSupabaseExam));
        }

        if (accountantsData) {
          if (isCancelled && isCancelled()) return;
          setAccountantAccesses(accountantsData.map(mapSupabaseAccountantAccess));
        }

        // Se houver rascunho de visitante e o usuário logado não tiver rascunho próprio, migra o rascunho!
        const guestMessages = localStorage.getItem('rmax_draft_messages_guest');
        const userMessages = localStorage.getItem(`rmax_draft_messages_${userId}`);
        if (guestMessages && !userMessages) {
          localStorage.setItem(`rmax_draft_messages_${userId}`, guestMessages);
          const guestForm = localStorage.getItem('rmax_draft_form_guest');
          if (guestForm) localStorage.setItem(`rmax_draft_form_${userId}`, guestForm);
          const guestReady = localStorage.getItem('rmax_draft_ready_guest');
          if (guestReady) localStorage.setItem(`rmax_draft_ready_${userId}`, guestReady);
          const guestExplanation = localStorage.getItem('rmax_draft_explanation_guest');
          if (guestExplanation) localStorage.setItem(`rmax_draft_explanation_${userId}`, guestExplanation);
        }

        if (isCancelled && isCancelled()) return;
        const initialUrlView = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
        if (initialUrlView && initialUrlView !== 'chat') {
          setViewRef.current(initialUrlView as AppView);
        } else {
          const defaultV = resolveDefaultViewForUser(mappedUser.plan, mappedUser.isMasterUser, mappedUser.isTrialActive);
          setViewRef.current(defaultV);
          window.history.replaceState({ ...(window.history.state || {}), view: defaultV }, '', '');
        }
      } else {
        const state = loadState();
        if (isCancelled && isCancelled()) return;
        if (state.user && state.financialConfig) {
          setUser(state.user);
          setConfig(state.financialConfig);
          setQuotes(state.quotes || []);
          setMaterials(state.materials || []);
          setRecipes(state.recipes || []);
          setServices(state.services || []);
          setClients(state.clients || []);
          setSuppliers(state.suppliers || []);
          setWorkOrders(state.workOrders || []);
          setReceipts(state.receipts || []);
          setAppointments(state.appointments || []);
          setStaffMembers(state.staffMembers || []);
          setSubscriptionClubs(state.subscriptionClubs || []);
          setStorefrontProfile(state.storefrontProfile || null);
          if (state.inventoryBatches) setInventoryBatches(state.inventoryBatches);
          if (state.stockMovements) setStockMovements(state.stockMovements);
          if (state.clinicalPatients) setClinicalPatients(state.clinicalPatients);
          if (state.clinicalConsultations) setClinicalConsultations(state.clinicalConsultations);
          if (state.clinicalVaccinations) setClinicalVaccinations(state.clinicalVaccinations);
          if (state.clinicalExams) setClinicalExams(state.clinicalExams);

          const initialUrlView = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
          if (initialUrlView && initialUrlView !== 'chat') {
            setViewRef.current(initialUrlView as AppView);
          } else {
            const defaultV = resolveDefaultViewForUser(state.user.plan, state.user.isMasterUser, state.user.isTrialActive);
            setViewRef.current(defaultV);
            window.history.replaceState({ ...(window.history.state || {}), view: defaultV }, '', '');
          }
        } else {
          // COMPLETELY NEW USER: Initialize temporary guest-like profile mapped to their session user ID
          const tempUser: UserProfile = {
            id: userId,
            ownerName: sessionRef.current?.user?.user_metadata?.full_name || 'Novo Empreendedor',
            email: sessionRef.current?.user?.email || '',
            businessName: '',
            niche: 'Geral',
            plan: 'Grátis',
            actingType: 'Novo na área'
          };
          const tempConfig: FinancialConfig = {
            userId: userId,
            proLaboreGoal: 0,
            monthlyFixedCost: 0,
            weeklyWorkHours: 40,
            baseHourValue: 0,
            paymentFeePercentage: 0,
            defaultCommissionPercentage: 0
          };
          setUser(tempUser);
          setConfig(tempConfig);
          setClients([]);
          setSuppliers([]);
          setWorkOrders([]);
          setReceipts([]);
          setAppointments([]);
          setStaffMembers([]);
          setSubscriptionClubs([]);
          setStorefrontProfile(null);
          const initialUrlView = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
          if (initialUrlView && initialUrlView !== 'chat') {
            setViewRef.current(initialUrlView as AppView);
          } else {
            const pendingP = safeStorage.getItem('rmax_pending_plan');
            const defaultV = resolveDefaultViewForUser(pendingP || tempUser.plan, tempUser.isMasterUser, tempUser.isTrialActive);
            setViewRef.current(defaultV);
            window.history.replaceState({ ...(window.history.state || {}), view: defaultV }, '', '');
          }
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      if (isCancelled && isCancelled()) return;
      const state = loadState();
      if (state.user && state.financialConfig && !userRef.current) {
        setUser(state.user);
        setConfig(state.financialConfig);
        setQuotes(state.quotes || []);
        setMaterials(state.materials || []);
        setRecipes(state.recipes || []);
        setServices(state.services || []);
        setClients(state.clients || []);
        setSuppliers(state.suppliers || []);
        setWorkOrders(state.workOrders || []);
        setReceipts(state.receipts || []);
        setAppointments(state.appointments || []);
        setStaffMembers(state.staffMembers || []);
        setSubscriptionClubs(state.subscriptionClubs || []);
        setStorefrontProfile(state.storefrontProfile || null);
      }
    } finally {
      if (isCancelled && isCancelled()) return;
      setIsLoadingUserData(false);
      setHasLoadedAllData(true);
      lastLoadedId.current = userId;
    }
  }, []);

  // Load guest data / trigger user load
  useEffect(() => {
    let cancelled = false;
    if (session?.user) {
      loadUserData(session.user.id, () => cancelled);
    } else {
      activeUserIdRef.current = 'guest';
      lastLoadedId.current = null;
      // MODO VISITANTE (Atrito Zero)
      setUser(getGuestUser());
      setConfig(GUEST_CONFIG);
      setQuotes([]);
      setMaterials([]);
      setRecipes([]);
      setServices([]);
      setClients([]);
      setSuppliers([]);
      setWorkOrders([]);
      setReceipts([]);
      setAppointments([]);
      setStaffMembers([]);
      setSubscriptionClubs([]);
      const initialUrlView = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
      if (initialUrlView && initialUrlView !== 'chat') {
        setViewRef.current(initialUrlView as AppView);
      } else {
        const isBulk = typeof window !== 'undefined' && (window.location.search.includes('retail_bulk') || window.location.pathname.includes('granel') || window.location.pathname.includes('emporio'));
        const pendingPlan = safeStorage.getItem('rmax_pending_plan');
        const defaultV = isBulk ? 'home' : resolveDefaultViewForUser(pendingPlan || undefined);
        setViewRef.current(defaultV);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, loadUserData]);
  const addToSyncQueue = React.useCallback((item: { id: string; table: string; action: 'upsert' | 'delete'; payload?: any }) => {
    const queueUserId = session?.user?.id;
    if (!queueUserId) return;

    try {
      const queueJson = localStorage.getItem('rmax_sync_queue');
      const queue: SyncQueueItem[] = queueJson ? JSON.parse(queueJson) : [];
      
      const existingIndex = queue.findIndex(q => q.userId === queueUserId && q.id === item.id && q.table === item.table);
      const newItem: any = { ...item, userId: queueUserId, timestamp: Date.now() };
      
      if (existingIndex > -1) {
        queue[existingIndex] = newItem;
      } else {
        queue.push(newItem);
      }
      
      localStorage.setItem('rmax_sync_queue', JSON.stringify(queue));
    } catch (err) {
      console.error('Error adding to sync queue:', err);
    }
  }, [session?.user?.id]);

  const safeUpsertToSupabase = React.useCallback(async (table: string, payload: Record<string, any>) => {
    if (!session?.user) return { error: { message: 'No user session' } as any };
    let currentPayload: Record<string, any> = { ...payload, user_id: session.user.id };
    let res = await supabase.from(table).upsert(currentPayload, { count: 'exact' });
    let error = res.error;
    
    // Auto-correção resiliente se uma coluna não existir no cache de esquema do Supabase
    if (!error && res.count === 0) error = { message: 'Row Level Security blocked the operation (count 0)' } as any;
    let retryCount = 0;
    while (error && error.message && error.message.includes('Could not find the') && error.message.includes('column of') && retryCount < 3) {
      const match = error.message.match(/Could not find the '([^']+)' column/);
      if (match && match[1]) {
        const badCol = match[1];
        if (table === 'work_orders' && badCol === 'device_data') break;

        console.warn(`[SupabaseSync] Coluna '${badCol}' ausente na tabela '${table}'. Re-tentando sem ela...`);
        delete currentPayload[badCol];
        const retryRes = await supabase.from(table).upsert(currentPayload, { count: 'exact' });
        error = retryRes.error;
        res.count = retryRes.count;
        retryCount++;
      } else {
        break;
      }
    }

    // Auto-correção resiliente se status violar constraint de check antiga no banco
    if (error && error.message && error.message.includes('violates check constraint') && error.message.includes('status_check')) {
      console.warn(`[SupabaseSync] Status '${currentPayload.status}' violou constraint em '${table}'. Normalizando...`);
      if (table === 'appointments') currentPayload.status = 'confirmed';
      if (table === 'work_orders') currentPayload.status = 'open';
      const retryRes = await supabase.from(table).upsert(currentPayload, { count: 'exact' });
      error = retryRes.error;
        res.count = retryRes.count;
    }

    return { error, finalPayload: currentPayload };
  }, [session?.user]);

  const processSyncQueue = React.useCallback(async () => {
    if (!session?.user) return;
    const queueJson = localStorage.getItem('rmax_sync_queue');
    if (!queueJson) return;
    
    let queue: SyncQueueItem[] = [];
    try {
      queue = JSON.parse(queueJson);
    } catch (err) {
      console.error('Error parsing sync queue:', err);
      return;
    }
    
    if (queue.length === 0) return;
    const { currentUserQueue, retainedQueue } = splitSyncQueueByUser(queue, session.user.id);
    const remainingQueue: SyncQueueItem[] = [...retainedQueue];
    queue = currentUserQueue;
    if (queue.length === 0) {
      localStorage.setItem('rmax_sync_queue', JSON.stringify(remainingQueue));
      return;
    }
    
    for (const item of queue) {
      try {
        let error: any = null;
        if (item.action === 'delete') {
          const res = await supabase.from(item.table).delete().eq('id', item.id);
          error = res.error;
        } else if (item.action === 'upsert') {
          if (item.table === 'product_templates') {
            const recipe: ProductTemplate = item.payload;
            const templateRes = await supabase.from('product_templates').upsert({
              id: recipe.id,
              user_id: session.user.id,
              name: recipe.name,
              execution_time_minutes: recipe.executionTimeMinutes,
              created_at: recipe.createdAt
            });
            error = templateRes.error;
            
            if (!error) {
              await supabase.from('template_items').delete().eq('template_id', recipe.id);
              const itemsToSync = (recipe.items || []).map(ti => ({
                template_id: recipe.id,
                material_id: ti.materialId,
                quantity: ti.quantity
              }));
              if (itemsToSync.length > 0) {
                const itemsRes = await supabase.from('template_items').insert(itemsToSync);
                if (itemsRes.error) {
                  console.error('Error syncing recipe items from queue:', itemsRes.error.message);
                }
              }
            }
          } else {
            const res = await safeUpsertToSupabase(item.table, item.payload);
            error = res.error;
          }
        }
        
        if (error) {
          console.error(`Error processing queue item for ${item.table} (${item.id}):`, error.message);
          const attempts = (item.attempts || 0) + 1;
          if (attempts < 3) {
            remainingQueue.push({ ...item, attempts });
          } else {
            console.warn(`[SyncQueue] Descartando item com erro persistente após 3 tentativas: ${item.table} (${item.id})`);
          }
        }
      } catch (err) {
        console.warn(`Failed to execute queue item for ${item.table} (${item.id}):`, err);
        const attempts = (item.attempts || 0) + 1;
        if (attempts < 3) {
          remainingQueue.push({ ...item, attempts });
        }
      }
    }
    
    localStorage.setItem('rmax_sync_queue', JSON.stringify(remainingQueue));
  }, [session?.user, safeUpsertToSupabase]);

  useEffect(() => {
    if (session?.user) {
      processSyncQueue();
    }
    
    const handleOnline = () => {
      processSyncQueue();
    };
    
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [session?.user, processSyncQueue]);


  const syncUserToSupabase = React.useCallback(async (newUser: UserProfile) => {
    if (!session?.user) return;
    try {
      const birthDateClean = (newUser.birthDate && typeof newUser.birthDate === 'string' && newUser.birthDate.trim() !== '') ? newUser.birthDate : null;
      const payload: Record<string, any> = {
        id: session.user.id,
        name: newUser.ownerName || session.user.user_metadata?.full_name || 'Usuário R MAX',
        business_name: newUser.businessName || null,
        email: newUser.email || session.user.email,
        birth_date: birthDateClean,
        tax_regime: newUser.taxRegime || null,
        journey_status: newUser.actingType || 'Novo na área',
        city: newUser.city || null,
        state: newUser.state || null,
        niche: newUser.niche || null,
        lgpd_consent: newUser.lgpdConsent || null,
        market_data_sharing_consent: newUser.marketDataSharingConsent !== undefined 
          ? newUser.marketDataSharingConsent 
          : Boolean(newUser.lgpdConsent?.marketDataSharingAccepted)
      };

      let attempts = 0;
      while (attempts < 5) {
        const { error } = await supabase.from('users').upsert(payload, { onConflict: 'id' });
        if (!error) break;

        const match = error.message.match(/Could not find the '([^']+)' column/i);
        if (match && match[1] && match[1] in payload) {
          const missingCol = match[1];
          console.warn(`[users] Coluna '${missingCol}' ausente no Supabase. Removendo do payload e re-tentando...`);
          delete payload[missingCol];
          attempts++;
        } else {
          console.error('Error syncing user:', error.message);
          throw error;
        }
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error('Exception in user sync:', errMsg);
      throw err;
    }
  }, [session?.user]);

  const syncConfigToSupabase = React.useCallback(async (newConfig: FinancialConfig, newUser?: UserProfile) => {
    if (!session?.user) return;
    try {
      const currentUser = newUser || userRef.current;
      const payload: Record<string, any> = {
        user_id: session.user.id,
        owner_name: currentUser?.ownerName || null,
        business_name: currentUser?.businessName || null,
        business_address: currentUser?.businessNumber 
          ? `${currentUser.businessAddress}###${currentUser.businessNumber}` 
          : (currentUser?.businessAddress || null),
        zip_code: currentUser?.zipCode || null,
        show_address: currentUser?.showAddress ?? false,
        show_pix_key: currentUser?.showPixKey ?? (Boolean(currentUser?.pixKey && currentUser.pixKey.trim() !== '')),
        pro_labore_goal: newConfig.proLaboreGoal || 0,
        monthly_fixed_costs: newConfig.monthlyFixedCost || 0,
        base_hour_value: newConfig.baseHourValue || 0,
        weekly_work_hours: newConfig.weeklyWorkHours || 44,
        profession: currentUser?.profession || null,
        company_type: currentUser?.taxRegime || null,
        pix_key: currentUser?.pixKey || null,
        pix_key_type: currentUser?.pixKeyType || null,
        payment_fee_percentage: newConfig.paymentFeePercentage || 0,
        default_commission_percentage: newConfig.defaultCommissionPercentage || 0,
        niche: currentUser?.niche || null,
        last_niche_change: currentUser?.lastNicheChange || null,
        niche_change_count: currentUser?.nicheChangeCount ?? 0,
      };
      if (newConfig.logoUrl && typeof newConfig.logoUrl === 'string' && newConfig.logoUrl.trim() !== '') {
        payload.logo_url = newConfig.logoUrl;
      }

      let attempts = 0;
      while (attempts < 5) {
        const { error } = await supabase.from('user_business_profiles').upsert(payload, { onConflict: 'user_id' });
        if (!error) break;

        const match = error.message.match(/Could not find the '([^']+)' column/i);
        if (match && match[1] && match[1] in payload) {
          const missingCol = match[1];
          console.warn(`[user_business_profiles] Coluna '${missingCol}' ausente no Supabase. Removendo do payload e re-tentando...`);
          delete payload[missingCol];
          attempts++;
        } else {
          console.error('Error syncing business profile:', error.message);
          throw error;
        }
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error('Exception in config sync:', errMsg);
      throw err;
    }
  }, [session?.user]);

  const handleOnboardingComplete = React.useCallback(async (newUser: UserProfile, newConfig: FinancialConfig, targetView: AppView = 'home') => {
    const updatedUser = { ...newUser };
    const updatedConfig = { ...newConfig };
    
    setUser(updatedUser);
    setConfig(updatedConfig);
    
    if (session?.user) {
      updatedUser.id = session.user.id;
      updatedConfig.userId = session.user.id;
      try {
        await syncUserToSupabase(updatedUser);
        await syncConfigToSupabase(updatedConfig, updatedUser);
      } catch (err) {
        console.error('Error in onboarding sync:', err);
      }
    }

    const state = loadState();
    const existingQuotes = state.quotes || [];
    const existingMaterials = state.materials || [];
    const existingRecipes = state.recipes || [];
    const existingServices = state.services || [];
    setQuotes(existingQuotes);
    setMaterials(existingMaterials);
    setRecipes(existingRecipes);
    setServices(existingServices);
    saveState({ user: newUser, financialConfig: newConfig, quotes: existingQuotes, materials: existingMaterials, recipes: existingRecipes, services: existingServices });
    setIsFirstVisit(true);
    
    trackEvent('onboarding_complete', { 
      niche: newUser.niche, 
      actingType: newUser.actingType,
      city: newUser.city 
    });

    if (targetView === 'home') {
      setView('home'); 
      window.history.replaceState({ ...(window.history.state || {}), view: 'home' }, '', '');
    } else if (targetView === 'dashboard') {
      setView('dashboard');
      window.history.replaceState({ ...(window.history.state || {}), view: 'dashboard' }, '', '');
    } else {
      window.history.replaceState({ ...(window.history.state || {}), view: 'home' }, '', '');
      window.history.pushState({ view: targetView }, '');
      setView(targetView);
    }
  }, [session?.user, syncUserToSupabase, syncConfigToSupabase, setView]);

  const handleResetNicheData = React.useCallback(async () => {
    setQuotes([]);
    setMaterials([]);
    setRecipes([]);
    setServices([]);
    saveState({ user: userRef.current, financialConfig: config, quotes: [], materials: [], recipes: [], services: [] });

    if (session?.user?.id) {
      const uid = session.user.id;
      try {
        await Promise.allSettled([
          supabase.from('quotes').delete().eq('user_id', uid),
          supabase.from('materials').delete().eq('user_id', uid),
          supabase.from('product_templates').delete().eq('user_id', uid),
          supabase.from('simple_services').delete().eq('user_id', uid),
          supabase.from('user_cost_values').delete().eq('user_id', uid)
        ]);
      } catch (err) {
        console.error('Error executing Supabase niche data reset:', err);
      }
    }
  }, [session?.user?.id]);

  const handleProfileUpdate = React.useCallback(async (newUser: UserProfile, newConfig: FinancialConfig) => {
    if (userRef.current && userRef.current.businessName === 'R-MAX (Modo Teste)' && newUser.businessName !== 'R-MAX (Modo Teste)') {
      setIsFirstVisit(true);
    }
    setUser(newUser);
    setConfig(newConfig);

    // Propaga imediatamente o logotipo e nome comercial para a vitrine e agenda
    const resolvedLogo = newUser.logoUrl || (newUser as any).logoBase64;
    const resolvedBizName = newUser.businessName || newUser.ownerName;
    setStorefrontProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        displayName: resolvedBizName || prev.displayName,
        logoUrl: resolvedLogo || prev.logoUrl
      };
    });
    
    if (session?.user) {
      try {
        await syncUserToSupabase(newUser);
        await syncConfigToSupabase(newConfig, newUser);

        if (resolvedLogo || resolvedBizName) {
          const updatePayload: Record<string, any> = {};
          if (resolvedLogo) {
            updatePayload.logo_url = resolvedLogo;
            try {
              const palette = await extractDominantColor(resolvedLogo);
              if (palette && palette.primary && palette.primary !== '#059669') {
                updatePayload.accent_color = palette.primary;
              }
            } catch (err) {
              console.warn('Falha ao extrair paleta de cores da logo no perfil:', err);
            }
          }
          if (resolvedBizName) updatePayload.display_name = resolvedBizName;
          await supabase.from('storefront_profiles').update(updatePayload).eq('user_id', session.user.id);
        }
      } catch (err) {
        console.error('Error in handleProfileUpdate persistence:', err);
        throw err;
      }
    }
  }, [session?.user, syncUserToSupabase, syncConfigToSupabase]);

  const handleSaveQuote = React.useCallback(async (newQuote: SaveQuoteInput) => {
    const mappedQuote: Quote = {
      id: newQuote.id,
      userId: newQuote.user_id || newQuote.userId || '',
      clientName: newQuote.client_name || newQuote.clientName || '',
      clientPhone: newQuote.client_phone || newQuote.clientPhone,
      clientAddress: newQuote.client_address || newQuote.clientAddress,
      rawServiceName: newQuote.service_name || newQuote.rawServiceName,
      category: newQuote.category,
      materialCost: newQuote.material_cost !== undefined ? newQuote.material_cost : (newQuote.materialCost || 0),
      timeSpentMinutes: newQuote.time_spent_minutes !== undefined ? newQuote.time_spent_minutes : (newQuote.timeSpentMinutes || 0),
      appliedProfitMargin: newQuote.applied_profit_margin !== undefined ? newQuote.applied_profit_margin : (newQuote.appliedProfitMargin || 0),
      finalSuggestedPrice: newQuote.final_price !== undefined ? newQuote.final_price : (newQuote.finalSuggestedPrice || 0),
      finalPrice: newQuote.final_price !== undefined ? newQuote.final_price : (newQuote.finalPrice || 0),
      status: newQuote.status,
      createdAt: newQuote.created_at || newQuote.createdAt || new Date().toISOString(),
      closedAt: newQuote.closed_at || newQuote.closedAt,
      explanation: newQuote.explanation || '',
      statusHistory: Array.isArray(newQuote.status_history) ? newQuote.status_history : (newQuote.statusHistory || []),
      products: Array.isArray(newQuote.products) ? newQuote.products : [],
      services: Array.isArray(newQuote.services) ? newQuote.services : [],
      isPassThrough: newQuote.is_pass_through !== undefined ? newQuote.is_pass_through : newQuote.isPassThrough
    };

    setQuotes(prev => {
      const filtered = prev.filter(q => q.id !== mappedQuote.id);
      const next = [mappedQuote, ...filtered];
      quotesRef.current = next;
      return next;
    });

    if (session?.user) {
      const payload = {
        id: mappedQuote.id,
        client_name: mappedQuote.clientName,
        service_name: mappedQuote.rawServiceName,
        category: mappedQuote.category,
        material_cost: mappedQuote.materialCost,
        time_spent_minutes: mappedQuote.timeSpentMinutes,
        applied_profit_margin: mappedQuote.appliedProfitMargin,
        final_price: mappedQuote.finalPrice,
        status: mappedQuote.status,
        explanation: mappedQuote.explanation,
        status_history: mappedQuote.statusHistory,
        products: mappedQuote.products,
        services: mappedQuote.services,
        client_phone: mappedQuote.clientPhone,
        client_address: mappedQuote.clientAddress,
        is_pass_through: mappedQuote.isPassThrough,
        created_at: mappedQuote.createdAt
      };
      try {
        const { error } = await supabase.from('quotes').upsert({
          user_id: session.user.id,
          ...payload
        });
        if (error) {
          console.error('Error syncing quote:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) {
            addToSyncQueue({ id: mappedQuote.id, table: 'quotes', action: 'upsert', payload });
          }
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar orçamento:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) {
          addToSyncQueue({ id: mappedQuote.id, table: 'quotes', action: 'upsert', payload });
        }
      }
    }

    trackEvent('quote_finalized', {
      niche: newQuote.category,
      total_value: newQuote.final_price !== undefined ? newQuote.final_price : (newQuote.finalSuggestedPrice || 0),
      client_name: newQuote.client_name || newQuote.clientName
    });
  }, [session?.user, addToSyncQueue, handleAuthError]);

  const handleSaveRecipe = React.useCallback(async (newRecipe: ProductTemplate) => {
    setRecipes(prev => {
      const updated = prev.some(r => r.id === newRecipe.id)
        ? prev.map(r => r.id === newRecipe.id ? newRecipe : r)
        : [newRecipe, ...prev];
      return updated;
    });

    if (session?.user) {
      try {
        const { error: templateError } = await supabase.from('product_templates').upsert({
          id: newRecipe.id,
          user_id: session.user.id,
          name: newRecipe.name,
          execution_time_minutes: newRecipe.executionTimeMinutes,
          created_at: newRecipe.createdAt
        });

        if (!templateError) {
          await supabase.from('template_items').delete().eq('template_id', newRecipe.id);
          const itemsToSync = (newRecipe.items || []).map(item => ({
            template_id: newRecipe.id,
            material_id: item.materialId,
            quantity: item.quantity
          }));
          if (itemsToSync.length > 0) {
            const { error: itemsError } = await supabase.from('template_items').insert(itemsToSync);
            if (itemsError) {
              console.error('Error syncing recipe items:', itemsError.message);
              const isAuthErr = await handleAuthError(itemsError);
              if (!isAuthErr) {
                addToSyncQueue({ id: newRecipe.id, table: 'product_templates', action: 'upsert', payload: newRecipe });
              }
            }
          }
        } else {
          console.error('Error syncing recipe template:', templateError.message);
          const isAuthErr = await handleAuthError(templateError);
          if (!isAuthErr) {
            addToSyncQueue({ id: newRecipe.id, table: 'product_templates', action: 'upsert', payload: newRecipe });
          }
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar receita:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) {
          addToSyncQueue({ id: newRecipe.id, table: 'product_templates', action: 'upsert', payload: newRecipe });
        }
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  const handleDeleteRecipe = React.useCallback(async (id: string) => {
    setRecipes(prev => prev.filter(r => r.id !== id));

    if (session?.user) {
      try {
        const { error } = await supabase.from('product_templates').delete().eq('id', id);
        if (error) {
          console.error('Error deleting recipe:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) {
            addToSyncQueue({ id, table: 'product_templates', action: 'delete' });
          }
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar receita:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) {
          addToSyncQueue({ id, table: 'product_templates', action: 'delete' });
        }
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  const handleSaveService = React.useCallback(async (newService: SimpleService) => {
    const savedService = await useServiceStore.getState().saveService(newService);
    setServices(prev => prev.some(s => s.id === savedService.id)
      ? prev.map(s => s.id === savedService.id ? savedService : s)
      : [savedService, ...prev]);

    if (session?.user) {
      const businessName = user?.businessName || user?.ownerName || 'Minha Empresa';
      const rawSlug = businessName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const autoSlug = (rawSlug && rawSlug !== 'meu-negocio' && rawSlug !== 'minha-empresa' && rawSlug !== 'empresa')
        ? `${rawSlug}-${session.user.id.slice(0, 6)}`
        : `empresa-${session.user.id.slice(0, 6)}`;

      try {
        await supabase.from('storefront_profiles').upsert({
          user_id: session.user.id,
          slug: autoSlug,
          display_name: businessName,
          tagline: user?.profession || 'Atendimento Profissional',
          whatsapp_number: user?.phone || '',
          address_display: user?.businessAddress || '',
          accent_color: '#10b981',
          is_published: true,
          view_count: 0
        }, { onConflict: 'user_id' });
      } catch (err) {
        console.warn('[storefront_profiles] Sync silencioso de perfil após serviço:', err);
      }
    }
  }, [session?.user, user]);

  const handleDeleteService = React.useCallback(async (id: string) => {
    await useServiceStore.getState().deleteService(id);
    setServices(prev => prev.filter(service => service.id !== id));
  }, []);

  const handleSaveMaterials = React.useCallback(async (newMaterials: Material[]) => {
    const oldMaterials = materialsRef.current;
    // Note: newMaterials is the complete and canonical replacement array of materials, not a delta.
    // Using functional updater ensures proper React batch scheduling order, while updating
    // the synchronous ref prevents race conditions during rapid consecutive saves.
    setMaterials(prev => newMaterials);
    materialsRef.current = newMaterials;

    const calculatedMonthlyFixedCost = newMaterials
      .filter(m => m.category === 'fixo')
      .reduce((acc, m) => {
        const monthlyImpact = (m.depreciationMonths != null && m.depreciationMonths > 0)
          ? m.pricePaid / m.depreciationMonths
          : m.pricePaid;
        return acc + monthlyImpact;
      }, 0);

    if (config && Math.abs(config.monthlyFixedCost - calculatedMonthlyFixedCost) > 0.01) {
      const updatedConfig = { ...config, monthlyFixedCost: calculatedMonthlyFixedCost };
      setConfig(updatedConfig);
      
      if (session?.user) {
        try {
          const { error } = await supabase.from('user_business_profiles').upsert({
            user_id: session.user.id,
            monthly_fixed_costs: calculatedMonthlyFixedCost
          }, {
            onConflict: 'user_id'
          });
          if (error) console.error('Error syncing CFM:', error.message);
        } catch (err) {
          console.warn('R MAX: Falha de rede ao sincronizar CFM:', err);
        }
      }
    }
    
    if (session?.user) {
      try {
        const newIds = new Set(newMaterials.map(m => m.id));
        const deletedMaterials = oldMaterials.filter(m => !newIds.has(m.id));

        // Clean up local recipes state items for deleted materials
        if (deletedMaterials.length > 0) {
          const deletedIds = new Set(deletedMaterials.map(m => m.id));
          setRecipes(prevRecipes =>
            prevRecipes.map(recipe => ({
              ...recipe,
              items: recipe.items ? recipe.items.filter(item => !deletedIds.has(item.materialId)) : []
            }))
          );
        }

        // 1. Delete materials that are no longer in newMaterials
        for (const m of deletedMaterials) {
          if (m.id) {
            try {
              const { error } = await supabase.from('materials').delete().eq('id', m.id);
              if (error) {
                console.error('Error deleting material:', error.message);
                const isAuthErr = await handleAuthError(error);
                const isFkErr = error.code === '23503' || (error as any).status === 409;
                if (!isAuthErr && !isFkErr) {
                  addToSyncQueue({ id: m.id, table: 'materials', action: 'delete' });
                }
              }
            } catch (err) {
              console.warn('R MAX: Falha de rede ao deletar material:', err);
              const isAuthErr = await handleAuthError(err);
              if (!isAuthErr) {
                addToSyncQueue({ id: m.id, table: 'materials', action: 'delete' });
              }
            }
          }
        }

        // 2. Upsert materials that are new or changed
        for (const m of newMaterials) {
          const old = oldMaterials.find(om => om.id === m.id);
          const isChanged = !old ||
            old.name !== m.name ||
            old.brand !== m.brand ||
            old.category !== m.category ||
            old.unit !== m.unit ||
            old.pricePaid !== m.pricePaid ||
            old.yieldAmount !== m.yieldAmount ||
            old.depreciationMonths !== m.depreciationMonths ||
            old.imageUrl !== m.imageUrl ||
            old.itemType !== m.itemType ||
            old.salePrice !== m.salePrice ||
            old.marginPercent !== m.marginPercent ||
            old.stockQuantity !== m.stockQuantity ||
            old.barcode !== m.barcode ||
            old.sku !== m.sku ||
            old.ncm !== m.ncm ||
            old.cest !== m.cest ||
            old.originCode !== m.originCode ||
            old.minStock !== m.minStock ||
            old.location !== m.location ||
            old.weightKg !== m.weightKg ||
            JSON.stringify(old.dimensionsCm) !== JSON.stringify(m.dimensionsCm) ||
            old.nicheCategory !== m.nicheCategory || old.supplierId !== m.supplierId || old.supplierName !== m.supplierName || old.stockControlMode !== m.stockControlMode || old.packQuantity !== m.packQuantity ||
            JSON.stringify(old.nicheMetadata) !== JSON.stringify(m.nicheMetadata);

          if (isChanged) {
            const isUUID = m.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(m.id);
            const finalId = isUUID ? m.id : crypto.randomUUID();
            const payload = {
              id: finalId,
              name: m.name,
              brand: m.brand?.trim() || null,
              category: m.category,
              unit: m.unit,
              price: m.pricePaid,
              yield: m.yieldAmount,
              depreciation_months: m.depreciationMonths || 0,
              image_url: m.imageUrl || null,
              item_type: m.itemType || (m.category === 'variável' && (m.salePrice || 0) > 0 ? 'produto_venda' : 'insumo'),
              sale_price: m.salePrice ?? null,
              margin_percent: m.marginPercent ?? null,
              stock_quantity: m.stockQuantity ?? 0,
              barcode_ean: m.barcode?.trim() || null,
              sku: m.sku?.trim() || null,
              ncm: m.ncm?.trim() || null,
              cest: m.cest?.trim() || null,
              origin_code: m.originCode ?? 0,
              min_stock: m.minStock ?? 0,
              location: m.location?.trim() || null,
              weight_kg: m.weightKg ?? null,
              dimensions_cm: m.dimensionsCm || null,
              niche_category: m.nicheCategory?.trim() || null,
              niche_metadata: { ...(m.nicheMetadata || {}), ...(m.supplierId ? { supplierId: m.supplierId } : {}), ...(m.supplierName ? { supplierName: m.supplierName.trim() } : {}), ...(m.stockControlMode ? { stockControlMode: m.stockControlMode } : {}), ...(m.packQuantity != null && m.packQuantity > 0 ? { packQuantity: m.packQuantity } : {}) }
            };

            try {
              const { error } = await supabase.from('materials').upsert({
                user_id: session.user.id,
                ...payload
              });
              if (error) {
                console.error('Error syncing material:', error.message);
                const isAuthErr = await handleAuthError(error);
                if (!isAuthErr) {
                  addToSyncQueue({ id: finalId, table: 'materials', action: 'upsert', payload });
                }
              }
            } catch (err) {
              console.warn('R MAX: Falha de rede ao salvar material:', err);
              const isAuthErr = await handleAuthError(err);
              if (!isAuthErr) {
                addToSyncQueue({ id: finalId, table: 'materials', action: 'upsert', payload });
              }
            }
          }
        }

        trackEvent('material_cost_updated', {
          count: newMaterials.length,
          items: newMaterials.map(m => ({ name: m.name, price: m.pricePaid }))
        });
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar materiais:', err);
      }
    }
  }, [session?.user, config, addToSyncQueue, handleAuthError]);

  const handleUpdateQuoteStatus = React.useCallback(async (id: string, status: Quote['status']) => {
    setQuotes(prev => {
      const next = prev.map((q) => {
        if (q.id === id) {
          const newHistory = [
            ...(q.statusHistory || []),
            { status, timestamp: new Date().toISOString() }
          ];
          return { 
            ...q, 
            status, 
            closedAt: status === 'approved' ? new Date().toISOString() : q.closedAt,
            statusHistory: newHistory
          };
        }
        return q;
      });
      quotesRef.current = next;
      return next;
    });

    if (session?.user) {
      const q = quotesRef.current.find(quote => quote.id === id);
      if (q) {
        const newHistory = [
          ...(q.statusHistory || []),
          { status, timestamp: new Date().toISOString() }
        ];
        const payload = {
          id: q.id,
          client_name: q.clientName,
          service_name: q.rawServiceName,
          category: q.category,
          material_cost: q.materialCost,
          time_spent_minutes: q.timeSpentMinutes,
          applied_profit_margin: q.appliedProfitMargin,
          final_price: q.finalPrice,
          status,
          explanation: q.explanation,
          status_history: newHistory,
          products: q.products,
          services: q.services,
          client_phone: q.clientPhone,
          client_address: q.clientAddress,
          is_pass_through: q.isPassThrough,
          created_at: q.createdAt,
          closed_at: status === 'approved' ? new Date().toISOString() : q.closedAt
        };
        try {
          const { error } = await supabase.from('quotes').update({ 
            status, 
            closed_at: status === 'approved' ? new Date().toISOString() : null,
            status_history: newHistory
          }).eq('id', id);
          if (error) {
            console.error('Error updating quote status:', error.message);
            const isAuthErr = await handleAuthError(error);
            if (!isAuthErr) {
              addToSyncQueue({ id, table: 'quotes', action: 'upsert', payload });
            }
          }
        } catch (err) {
          console.warn('R MAX: Falha de rede ao atualizar status do orçamento:', err);
          const isAuthErr = await handleAuthError(err);
          if (!isAuthErr) {
            addToSyncQueue({ id, table: 'quotes', action: 'upsert', payload });
          }
        }
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // CRM: Clientes
  const handleSaveClient = React.useCallback(async (client: Client) => {
    setClients(prev => {
      const exists = prev.some(c => c.id === client.id);
      return exists ? prev.map(c => c.id === client.id ? client : c) : [client, ...prev];
    });

    if (session?.user) {
      const isUUID = client.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(client.id);
      const finalId = isUUID ? client.id : crypto.randomUUID();
      const payload = {
        id: finalId,
        name: client.name,
        phone: client.phone || null,
        email: client.email || null,
        document: client.document || null,
        address: client.address || null,
        tags: client.tags || [],
        total_spent: client.totalSpent || 0,
        orders_count: client.ordersCount || 0,
        source: client.source || 'manual',
        notes: client.notes || null,
        vehicle_data: client.vehicleData || null,
        vehicle_history: client.vehicleHistory || [],
        beauty_card: client.beautyCard || null,
        company_data: client.companyData || null,
        last_interaction_at: client.lastInteractionAt || new Date().toISOString(),
        subscription_plan_id: client.subscriptionPlanId || null,
        subscription_plan_name: client.subscriptionPlanName || null,
        subscription_status: client.subscriptionStatus || null,
        loyalty_points: client.loyaltyPoints || 0,
        created_at: client.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error, finalPayload } = await safeUpsertToSupabase('clients', payload);
        if (error) {
          console.error('Error syncing client:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'clients', action: 'upsert', payload: finalPayload || payload });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar cliente:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'clients', action: 'upsert', payload });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError, safeUpsertToSupabase]);

  const handleDeleteClient = React.useCallback(async (id: string) => {
    setClients(prev => prev.filter(c => c.id !== id));
    if (session?.user) {
      try {
        const { error } = await supabase.from('clients').delete().eq('id', id);
        if (error) {
          console.error('Error deleting client:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'clients', action: 'delete' });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar cliente:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'clients', action: 'delete' });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // Fornecedores
  const handleSaveSupplier = React.useCallback(async (supplier: Supplier) => {
    setSuppliers(prev => {
      const exists = prev.some(s => s.id === supplier.id);
      return exists ? prev.map(s => s.id === supplier.id ? supplier : s) : [supplier, ...prev];
    });

    if (session?.user) {
      const isUUID = supplier.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(supplier.id);
      const finalId = isUUID ? supplier.id : crypto.randomUUID();
      const payload = {
        id: finalId,
        name: supplier.name,
        trade_name: supplier.tradeName || null,
        cnpj: supplier.cnpj || null,
        ie: supplier.ie || null,
        cnae: supplier.cnae || null,
        category: supplier.category || 'Geral & Outros',
        phone: supplier.phone || null,
        email: supplier.email || null,
        address: supplier.address || null,
        payment_terms: supplier.paymentTerms || null,
        rating: supplier.rating || 5,
        notes: supplier.notes || null,
        source: supplier.source || 'manual',
        supplied_products_count: supplier.suppliedProductsCount || 0,
        total_purchased: supplier.totalPurchased || 0,
        created_at: supplier.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error, finalPayload } = await safeUpsertToSupabase('suppliers', payload);
        if (error) {
          console.error('Error syncing supplier:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'suppliers', action: 'upsert', payload: finalPayload || payload });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar fornecedor:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'suppliers', action: 'upsert', payload });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError, safeUpsertToSupabase]);

  const handleDeleteSupplier = React.useCallback(async (id: string) => {
    setSuppliers(prev => prev.filter(s => s.id !== id));
    if (session?.user) {
      try {
        const { error } = await supabase.from('suppliers').delete().eq('id', id);
        if (error) {
          console.error('Error deleting supplier:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'suppliers', action: 'delete' });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar fornecedor:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'suppliers', action: 'delete' });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // Ordens de Serviço (OS)
  const handleSaveWorkOrder = React.useCallback(async (workOrder: WorkOrder) => {
    setWorkOrders(prev => {
      const exists = prev.some(w => w.id === workOrder.id);
      return exists ? prev.map(w => w.id === workOrder.id ? workOrder : w) : [workOrder, ...prev];
    });

    if (session?.user) {
      const isUUID = workOrder.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(workOrder.id);
      const finalId = isUUID ? workOrder.id : crypto.randomUUID();
      const payload = {
        id: finalId,
        quote_id: workOrder.quoteId || null,
        order_number: workOrder.orderNumber,
        title: workOrder.title,
        niche_key: workOrder.nicheKey || 'geral',
        status: workOrder.status || 'open',
        client_name: workOrder.clientName,
        client_phone: workOrder.clientPhone || null,
        client_address: workOrder.clientAddress || null,
        client_document: workOrder.clientDocument || null,
        scheduled_start: workOrder.scheduledStart || null,
        scheduled_end: workOrder.scheduledEnd || null,
        completed_at: workOrder.completedAt || null,
        checklist_data: workOrder.checklistData || [],
        items_breakdown: workOrder.itemsBreakdown || [],
        technical_report: workOrder.technicalReport || null,
        warranty_terms: workOrder.warrantyTerms || null,
        total_labor: workOrder.totalLabor || 0,
        total_materials: workOrder.totalMaterials || 0,
        final_price: workOrder.finalPrice || 0,
        payment_terms: workOrder.paymentTerms || null,
        notes: workOrder.notes || null,
        vehicle_data: workOrder.vehicleData || null,
        device_data: workOrder.deviceData || null,
        hvac_data: workOrder.hvacData || null,
        beauty_data: workOrder.beautyData || null,
        laser_data: workOrder.laserData || null,
        photos: workOrder.photos || [],
        signatures: workOrder.signatures || [],
        created_at: workOrder.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error, finalPayload } = await safeUpsertToSupabase('work_orders', payload);
        if (error) {
          console.error('Error syncing work order:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'work_orders', action: 'upsert', payload: finalPayload || payload });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar OS:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'work_orders', action: 'upsert', payload });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError, safeUpsertToSupabase]);

  const handleDeleteWorkOrder = React.useCallback(async (id: string) => {
    setWorkOrders(prev => prev.filter(w => w.id !== id));
    if (session?.user) {
      try {
        const { error } = await supabase.from('work_orders').delete().eq('id', id);
        if (error) {
          console.error('Error deleting work order:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'work_orders', action: 'delete' });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar OS:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'work_orders', action: 'delete' });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // Recibos PIX
  const handleSaveReceipt = React.useCallback(async (receipt: Receipt) => {
    setReceipts(prev => {
      const oldReceipt = prev.find(r => r.id === receipt.id);
      const isNew = !oldReceipt;
      const diff = isNew ? (receipt.amountPaid || 0) : ((receipt.amountPaid || 0) - (oldReceipt?.amountPaid || 0));

      if (isNew || diff !== 0) {
        setClients(prevClients => prevClients.map(c => {
          if (c.name.toLowerCase().trim() === receipt.clientName?.toLowerCase().trim() || 
             (receipt.clientDocument && c.document === receipt.clientDocument)) {
            return {
              ...c,
              totalSpent: Math.max(0, (c.totalSpent || 0) + diff),
              ordersCount: isNew ? (c.ordersCount || 0) + 1 : (c.ordersCount || 0),
              lastInteractionAt: new Date().toISOString()
            };
          }
          return c;
        }));
      }

      return isNew ? [receipt, ...prev] : prev.map(r => r.id === receipt.id ? receipt : r);
    });

    if (session?.user) {
      const isUUID = receipt.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.id);
      const finalId = isUUID ? receipt.id : crypto.randomUUID();
      const payload = {
        id: finalId,
        quote_id: receipt.quoteId || null,
        work_order_id: receipt.workOrderId || null,
        receipt_number: receipt.receiptNumber,
        total_service_value: receipt.totalServiceValue,
        amount_paid: receipt.amountPaid,
        remaining_balance: receipt.remainingBalance || 0,
        payment_method: receipt.paymentMethod || 'pix',
        receipt_type: receipt.receiptType || 'full',
        installment_number: receipt.installmentNumber || 1,
        total_installments: receipt.totalInstallments || 1,
        client_name: receipt.clientName,
        client_document: receipt.clientDocument || null,
        description: receipt.description,
        pix_qr_code_base64: receipt.pixQrCodeBase64 || null,
        pix_copia_cola: receipt.pixCopiaCola || null,
        warranty_period: receipt.warrantyPeriod || null,
        warranty_unit: receipt.warrantyUnit || 'dias',
        warranty_terms: receipt.warrantyTerms || null,
        issued_at: receipt.issuedAt || new Date().toISOString(),
        created_at: receipt.createdAt || new Date().toISOString()
      };

      try {
        const { error, finalPayload } = await safeUpsertToSupabase('receipts', payload);
        if (error) {
          console.error('Error syncing receipt:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'receipts', action: 'upsert', payload: finalPayload || payload });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar recibo:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'receipts', action: 'upsert', payload });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError, safeUpsertToSupabase]);

  const handleDeleteReceipt = React.useCallback(async (id: string) => {
    setReceipts(prev => prev.filter(r => r.id !== id));
    if (session?.user) {
      try {
        const { error } = await supabase.from('receipts').delete().eq('id', id);
        if (error) {
          console.error('Error deleting receipt:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'receipts', action: 'delete' });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar recibo:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'receipts', action: 'delete' });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // Agenda & Agendamentos
  const handleSaveAppointment = React.useCallback(async (appointment: Appointment) => {
    const isUUID = appointment.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(appointment.id);
    const finalId = isUUID ? appointment.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : appointment.id);
    const normalizedAppt: Appointment = { ...appointment, id: finalId };

    setAppointments(prev => {
      const exists = prev.some(a => a.id === finalId || a.id === appointment.id);
      return exists ? prev.map(a => (a.id === finalId || a.id === appointment.id) ? normalizedAppt : a) : [normalizedAppt, ...prev];
    });

    if (session?.user) {
      const payload = {
        id: finalId,
        client_name: normalizedAppt.clientName,
        client_phone: normalizedAppt.clientPhone || null,
        client_address: normalizedAppt.clientAddress || null,
        service_name: normalizedAppt.serviceName,
        service_id: normalizedAppt.serviceId || null,
        services_list: normalizedAppt.servicesList || [],
        duration_minutes: normalizedAppt.durationMinutes || 60,
        scheduled_date: normalizedAppt.scheduledDate,
        scheduled_time: normalizedAppt.scheduledTime,
        price: normalizedAppt.price || 0,
        status: normalizedAppt.status || 'confirmed',
        payment_status: normalizedAppt.paymentStatus || (normalizedAppt.status === 'completed' ? 'paid' : 'pending'),
        payment_method: normalizedAppt.paymentMethod || null,
        executed_at: normalizedAppt.executedAt || (normalizedAppt.status === 'completed' ? new Date().toISOString() : null),
        paid_at: normalizedAppt.paidAt || (normalizedAppt.paymentStatus === 'paid' ? new Date().toISOString() : null),
        created_by_role: normalizedAppt.createdByRole || 'admin',
        package_total_sessions: normalizedAppt.packageTotalSessions || 1,
        package_current_session: normalizedAppt.packageCurrentSession || 1,
        staff_member_id: normalizedAppt.staffMemberId || null,
        staff_member_name: normalizedAppt.staffMemberName || null,
        commission_percent: (normalizedAppt.commissionPercent !== undefined && normalizedAppt.commissionPercent !== null) ? normalizedAppt.commissionPercent : 50,
        is_recurring: Boolean(normalizedAppt.isRecurring),
        recurring_rule: normalizedAppt.recurringRule || null,
        recurring_group_id: normalizedAppt.recurringGroupId || null,
        notes: normalizedAppt.notes || null,
        reminder_sent: Boolean(normalizedAppt.reminderSent),
        history_timeline: normalizedAppt.historyTimeline || [],
        created_at: normalizedAppt.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error, finalPayload } = await safeUpsertToSupabase('appointments', payload);
        if (error) {
          console.error('Error syncing appointment:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'appointments', action: 'upsert', payload: finalPayload || payload });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao salvar agendamento:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id: finalId, table: 'appointments', action: 'upsert', payload });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError, safeUpsertToSupabase]);

  const handleDeleteAppointment = React.useCallback(async (id: string) => {
    setAppointments(prev => prev.filter(a => a.id !== id));
    if (session?.user) {
      try {
        const { error } = await supabase.from('appointments').delete().eq('id', id);
        if (error) {
          console.error('Error deleting appointment:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'appointments', action: 'delete' });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao deletar agendamento:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'appointments', action: 'delete' });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  const handleUpdateAppointmentStatus = React.useCallback(async (
    id: string, 
    status: AppointmentStatus, 
    extra?: { 
      paymentStatus?: 'pending' | 'paid' | 'refunded'; 
      paymentMethod?: 'pix' | 'dinheiro' | 'cartao_credito' | 'cartao_debito' | 'outro' | null; 
      paidAt?: string | null; 
      executedAt?: string | null;
      price?: number;
      notes?: string;
      servicesList?: any[];
    }
  ) => {
    const nowIso = new Date().toISOString();
    const updatedFields: any = { status, updatedAt: nowIso };
    if (extra?.paymentStatus !== undefined) {
      updatedFields.paymentStatus = extra.paymentStatus;
      if (extra.paymentStatus === 'pending') {
        updatedFields.paidAt = extra.paidAt !== undefined ? (extra.paidAt || undefined) : undefined;
        updatedFields.paymentMethod = extra.paymentMethod !== undefined ? (extra.paymentMethod || undefined) : undefined;
      }
    }
    if (extra?.paymentMethod !== undefined) updatedFields.paymentMethod = extra.paymentMethod || undefined;
    if (extra?.paidAt !== undefined) updatedFields.paidAt = extra.paidAt || undefined;
    if (extra?.executedAt !== undefined) updatedFields.executedAt = extra.executedAt || undefined;
    if (extra?.price !== undefined) updatedFields.price = extra.price;
    if (extra?.notes !== undefined) updatedFields.notes = extra.notes;
    if (extra?.servicesList !== undefined) updatedFields.servicesList = extra.servicesList;

    setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updatedFields } : a));
    if (session?.user) {
      const dbPayload: any = {
        status,
        updated_at: nowIso
      };
      if (extra?.paymentStatus !== undefined) {
        dbPayload.payment_status = extra.paymentStatus;
        if (extra.paymentStatus === 'pending') {
          dbPayload.paid_at = extra.paidAt !== undefined ? extra.paidAt : null;
          dbPayload.payment_method = extra.paymentMethod !== undefined ? extra.paymentMethod : null;
        }
      }
      if (extra?.paymentMethod !== undefined) dbPayload.payment_method = extra.paymentMethod || null;
      if (extra?.paidAt !== undefined) dbPayload.paid_at = extra.paidAt || null;
      if (extra?.executedAt !== undefined) dbPayload.executed_at = extra.executedAt || null;
      if (extra?.price !== undefined) dbPayload.price = extra.price;
      if (extra?.notes !== undefined) dbPayload.notes = extra.notes;
      if (extra?.servicesList !== undefined) dbPayload.services_list = extra.servicesList;

      try {
        const { error } = await supabase.from('appointments').update(dbPayload).eq('id', id);
        if (error) {
          console.error('Error updating appointment status:', error.message);
          const isAuthErr = await handleAuthError(error);
          if (!isAuthErr) addToSyncQueue({ id, table: 'appointments', action: 'upsert', payload: { id, ...dbPayload } });
        }
      } catch (err) {
        console.warn('R MAX: Falha de rede ao atualizar status do agendamento:', err);
        const isAuthErr = await handleAuthError(err);
        if (!isAuthErr) addToSyncQueue({ id, table: 'appointments', action: 'upsert', payload: { id, ...dbPayload } });
      }
    }
  }, [session?.user, addToSyncQueue, handleAuthError]);

  // Atualização rápida de agendamentos em tempo real / polling sem recarregar o app
  const refreshAppointments = React.useCallback(async (): Promise<Appointment[] | undefined> => {
    const activeWorkspaceId = user?.effectiveWorkspaceId || session?.user?.id;
    if (!activeWorkspaceId) return;

    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('user_id', activeWorkspaceId)
        .order('scheduled_date', { ascending: false })
        .limit(200);

      if (!error && data) {
        const mapped = data.map(mapSupabaseAppointment);
        setAppointments(mapped);
        return mapped;
      }
    } catch (err) {
      console.warn('R MAX: Falha ao atualizar agendamentos em segundo plano:', err);
    }
  }, [user?.effectiveWorkspaceId, session?.user?.id]);

  // Assinaturas Digitais
  const handleSaveSignature = React.useCallback(async (sig: DigitalSignature) => {
    if (session?.user) {
      const payload = {
        document_type: sig.documentType,
        document_id: sig.documentId,
        provider_name: sig.providerName,
        provider_signature_base64: sig.providerSignatureBase64 || null,
        client_name: sig.clientName,
        client_document: sig.clientDocument || null,
        client_signature_base64: sig.clientSignatureBase64,
        ip_address: sig.ipAddress || null,
        user_agent: sig.userAgent || navigator.userAgent,
        geolocation: sig.geolocation || null,
        verification_hash: sig.verificationHash,
        signed_at: sig.signedAt || new Date().toISOString()
      };

      try {
        const { error } = await supabase.from('digital_signatures').insert({
          user_id: session.user.id,
          ...payload
        });
        if (error) console.error('Error recording digital signature:', error.message);
      } catch (err) {
        console.warn('R MAX: Falha ao persistir assinatura digital:', err);
      }
    }
  }, [session?.user]);

  // Atendentes / Equipe
  const handleSaveStaffMember = React.useCallback(async (staff: StaffMember) => {
    const ensuredToken = staff.accessToken || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).substring(2) + Date.now().toString(36));
    const basePermissions = staff.permissions || {
      canCreateAppointments: true,
      canBlockSlots: true,
      canComplete: true,
      showClientPhone: true,
      showCommission: true
    };
    // Duplo fallback: persiste serviceIds também dentro do JSONB de permissions
    const permissionsWithServices = {
      ...basePermissions,
      serviceIds: staff.serviceIds || []
    };

    const isUUID = staff.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(staff.id);
    const finalId = isUUID ? staff.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : staff.id);

    const staffWithToken: StaffMember = {
      ...staff,
      id: finalId,
      accessToken: ensuredToken,
      permissions: permissionsWithServices
    };

    setStaffMembers(prev => {
      const exists = prev.some(s => s.id === finalId || s.id === staff.id || (s.accessToken && s.accessToken === ensuredToken));
      return exists ? prev.map(s => (s.id === finalId || s.id === staff.id || s.accessToken === ensuredToken) ? staffWithToken : s) : [...prev, staffWithToken];
    });

    if (session?.user) {
      const basePayload: any = {
        id: finalId,
        name: staffWithToken.name,
        role: staffWithToken.role,
        color: staffWithToken.color || 'emerald',
        commission_percent: staffWithToken.commissionPercent || 50,
        phone: staffWithToken.phone || null,
        access_token: staffWithToken.accessToken || ensuredToken,
        pin_code: staffWithToken.pinCode || null,
        permissions: permissionsWithServices,
        avatar_url: staffWithToken.avatarUrl || null,
        active: staffWithToken.active !== undefined ? staffWithToken.active : true,
        created_at: staffWithToken.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error } = await supabase.from('staff_members').upsert({
          user_id: session.user.id,
          ...basePayload,
          service_ids: staffWithToken.serviceIds || []
        });

        if (error) {
          // Caso a coluna service_ids ainda não exista no Supabase
          if (error.message?.includes('service_ids') || error.code === '42703' || error.code === 'PGRST204') {
            await supabase.from('staff_members').upsert({
              user_id: session.user.id,
              ...basePayload
            });
          } else if (error.code === '23505' || error.message?.includes('access_token')) {
            // Se houver conflito de access_token com outro ID antigo no banco, atualiza por access_token
            await supabase.from('staff_members')
              .update({
                name: staffWithToken.name,
                role: staffWithToken.role,
                color: staffWithToken.color || 'emerald',
                commission_percent: staffWithToken.commissionPercent || 50,
                phone: staffWithToken.phone || null,
                pin_code: staffWithToken.pinCode || null,
                permissions: permissionsWithServices,
                service_ids: staffWithToken.serviceIds || [],
                avatar_url: staffWithToken.avatarUrl || null,
                active: staffWithToken.active !== undefined ? staffWithToken.active : true,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', session.user.id)
              .eq('access_token', ensuredToken);
          }
        }
      } catch (err) {
        console.warn('R MAX: Falha ao salvar atendente:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteStaffMember = React.useCallback(async (id: string) => {
    setStaffMembers(prev => prev.filter(s => s.id !== id));
    if (session?.user) {
      try {
        await supabase.from('staff_members').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar atendente:', err);
      }
    }
  }, [session?.user]);

  // Vitrine Digital
  const handleSaveStorefrontProfile = React.useCallback(async (profile: StorefrontProfile) => {
    setStorefrontProfile(profile);
    if (profile.businessHours) {
      safeStorage.setItem('rmax_weekly_business_hours', profile.businessHours);
    }
    safeStorage.setItem('rmax_storefront_profile', JSON.stringify(profile));

    if (session?.user) {
      const payload = {
        slug: profile.slug,
        display_name: profile.displayName,
        tagline: profile.tagline || null,
        bio: profile.bio || null,
        years_experience: profile.yearsExperience || null,
        niche_badges: profile.nicheBadges || [],
        whatsapp_number: profile.whatsappNumber,
        instagram_handle: profile.instagramHandle || null,
        facebook_handle: profile.facebookHandle || null,
        tiktok_handle: profile.tiktokHandle || null,
        email: profile.email || null,
        address_display: profile.addressDisplay || null,
        accent_color: profile.accentColor || '#10b981',
        logo_url: profile.logoUrl || null,
        cover_url: profile.coverUrl || null,
        business_hours: profile.businessHours || null,
        is_published: profile.isPublished !== undefined ? profile.isPublished : true,
        view_count: profile.viewCount || 0,
        created_at: profile.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        const { error } = await supabase.from('storefront_profiles').upsert({
          user_id: session.user.id,
          ...payload
        }, { onConflict: 'user_id' });

        if (error && error.message && error.message.includes('business_hours')) {
          const fallback = { ...payload };
          delete (fallback as any).business_hours;
          await supabase.from('storefront_profiles').upsert({
            user_id: session.user.id,
            ...fallback
          }, { onConflict: 'user_id' });
        }
      } catch (err) {
        console.warn('R MAX: Falha ao salvar perfil da vitrine:', err);
      }
    }
  }, [session?.user]);

  // Clubes VIP
  const handleSaveSubscriptionClub = React.useCallback(async (club: SubscriptionClubPlan) => {
    setSubscriptionClubs(prev => {
      const exists = prev.some(c => c.id === club.id);
      return exists ? prev.map(c => c.id === club.id ? club : c) : [...prev, club];
    });

    if (session?.user) {
      const isUUID = club.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(club.id);
      const finalId = isUUID ? club.id : crypto.randomUUID();
      const payload = {
        id: finalId,
        name: club.name,
        price_monthly: club.priceMonthly,
        description: club.description || null,
        benefits: club.benefits || [],
        max_services_per_month: club.maxServicesPerMonth || null,
        eligible_services: club.eligibleServices || [],
        active_subscribers_count: club.activeSubscribersCount || 0,
        created_at: club.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        await supabase.from('subscription_clubs').upsert({
          user_id: session.user.id,
          ...payload
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar clube VIP:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteSubscriptionClub = React.useCallback(async (id: string) => {
    setSubscriptionClubs(prev => prev.filter(c => c.id !== id));
    if (session?.user) {
      try {
        await supabase.from('subscription_clubs').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar clube VIP:', err);
      }
    }
  }, [session?.user]);

  // ────────────────────────────────────────────────────────────────────────────
  // ENTERPRISE HANDLERS: ESTOQUE E GESTÃO CLÍNICA MULTI-PET
  // ────────────────────────────────────────────────────────────────────────────
  const handleSaveInventoryBatch = React.useCallback(async (batch: InventoryBatch) => {
    setInventoryBatches(prev => {
      const exists = prev.some(b => b.id === batch.id);
      return exists ? prev.map(b => b.id === batch.id ? batch : b) : [...prev, batch];
    });

    if (session?.user) {
      const isUUID = batch.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(batch.id);
      const finalId = isUUID ? batch.id : crypto.randomUUID();
      try {
        await supabase.from('inventory_batches').upsert({
          id: finalId,
          user_id: session.user.id,
          material_id: batch.materialId,
          batch_code: batch.batchCode,
          expiration_date: batch.expirationDate,
          cost_price: batch.costPrice,
          initial_quantity: batch.initialQuantity,
          current_quantity: batch.currentQuantity
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar lote de estoque:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteInventoryBatch = React.useCallback(async (id: string) => {
    setInventoryBatches(prev => prev.filter(b => b.id !== id));
    if (session?.user) {
      try {
        await supabase.from('inventory_batches').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar lote:', err);
      }
    }
  }, [session?.user]);

  const handleSaveStockMovement = React.useCallback(async (movement: StockMovement) => {
    setStockMovements(prev => [movement, ...prev]);
    if (session?.user) {
      const isUUID = movement.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(movement.id);
      const finalId = isUUID ? movement.id : crypto.randomUUID();
      try {
        await supabase.from('stock_movements').insert({
          id: finalId,
          user_id: session.user.id,
          material_id: movement.materialId,
          batch_id: movement.batchId || null,
          type: movement.type,
          quantity: movement.quantity,
          unit_cost: movement.unitCost,
          balance_after: movement.balanceAfter,
          reason_notes: movement.reasonNotes || null,
          reference_id: movement.referenceId || null
        });
      } catch (err) {
        console.warn('R MAX: Falha ao registrar movimentação de estoque:', err);
      }
    }
  }, [session?.user]);

  const handleSaveClinicalPatient = React.useCallback(async (patient: ClinicalPatient) => {
    setClinicalPatients(prev => {
      const exists = prev.some(p => p.id === patient.id);
      return exists ? prev.map(p => p.id === patient.id ? patient : p) : [patient, ...prev];
    });

    if (session?.user) {
      const isUUID = patient.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(patient.id);
      const finalId = isUUID ? patient.id : crypto.randomUUID();
      try {
        await supabase.from('clinical_patients').upsert({
          id: finalId,
          user_id: session.user.id,
          client_id: patient.clientId,
          kind: patient.kind || 'pet',
          name: patient.name,
          species: patient.species,
          breed: patient.breed || null,
          gender: patient.gender,
          is_neutered: patient.isNeutered,
          birth_date: patient.birthDate || null,
          microchip_number: patient.microchipNumber || null,
          coat_color: patient.coatColor || null,
          known_allergies: patient.knownAllergies || null,
          chronic_conditions: patient.chronicConditions || null,
          active: patient.active
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar paciente clínico:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteClinicalPatient = React.useCallback(async (id: string) => {
    setClinicalPatients(prev => prev.filter(p => p.id !== id));
    if (session?.user) {
      try {
        await supabase.from('clinical_patients').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar paciente clínico:', err);
      }
    }
  }, [session?.user]);

  const handleSaveClinicalConsultation = React.useCallback(async (consultation: ClinicalConsultation) => {
    setClinicalConsultations(prev => {
      const exists = prev.some(c => c.id === consultation.id);
      return exists ? prev.map(c => c.id === consultation.id ? consultation : c) : [consultation, ...prev];
    });

    if (session?.user) {
      const isUUID = consultation.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(consultation.id);
      const finalId = isUUID ? consultation.id : crypto.randomUUID();
      try {
        await supabase.from('clinical_consultations').upsert({
          id: finalId,
          user_id: session.user.id,
          patient_id: consultation.patientId,
          appointment_id: consultation.appointmentId || null,
          staff_member_id: consultation.staffMemberId || null,
          subjective_anamnesis: consultation.subjectiveAnamnesis || '',
          objective_exam: consultation.objectiveExam || {},
          assessment_diagnosis: consultation.assessmentDiagnosis || null,
          plan_treatment: consultation.planTreatment || '',
          prescriptions: consultation.prescriptions || [],
          consumed_materials: consultation.consumedMaterials || [],
          consultation_fee: consultation.consultationFee,
          materials_fee: consultation.materialsFee,
          status: consultation.status,
          finalized_at: consultation.finalizedAt || null,
          professional_signature_hash: consultation.professionalSignatureHash || null
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar prontuário SOAP:', err);
      }
    }
  }, [session?.user]);

  const handleSaveClinicalVaccination = React.useCallback(async (vaccination: ClinicalVaccination) => {
    setClinicalVaccinations(prev => {
      const exists = prev.some(v => v.id === vaccination.id);
      return exists ? prev.map(v => v.id === vaccination.id ? vaccination : v) : [...prev, vaccination];
    });

    if (session?.user) {
      const isUUID = vaccination.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(vaccination.id);
      const finalId = isUUID ? vaccination.id : crypto.randomUUID();
      try {
        await supabase.from('clinical_vaccinations').upsert({
          id: finalId,
          user_id: session.user.id,
          patient_id: vaccination.patientId,
          vaccine_name: vaccination.vaccineName,
          applied_at: vaccination.appliedAt,
          next_due_date: vaccination.nextDueDate,
          reminder_count: vaccination.reminderCount || 0,
          last_reminded_at: vaccination.lastRemindedAt || null,
          status: vaccination.status || 'pending'
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar vacinação:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteClinicalVaccination = React.useCallback(async (id: string) => {
    setClinicalVaccinations(prev => prev.filter(v => v.id !== id));
    if (session?.user) {
      try {
        await supabase.from('clinical_vaccinations').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar vacina:', err);
      }
    }
  }, [session?.user]);

  const handleSaveClinicalExam = React.useCallback(async (exam: ClinicalExamVault) => {
    setClinicalExams(prev => {
      const exists = prev.some(e => e.id === exam.id);
      return exists ? prev.map(e => e.id === exam.id ? exam : e) : [exam, ...prev];
    });

    if (session?.user) {
      const isUUID = exam.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(exam.id);
      const finalId = isUUID ? exam.id : crypto.randomUUID();
      try {
        await supabase.from('clinical_exams_vault').upsert({
          id: finalId,
          user_id: session.user.id,
          patient_id: exam.patientId,
          consultation_id: exam.consultationId || null,
          exam_type: exam.examType,
          title: exam.title,
          file_url: exam.reportPdfUrl || exam.previewImageUrl || null,
          raw_dicom_url: exam.rawDicomR2Path || null,
          preview_url: exam.previewImageUrl || null,
          legal_retention_date: exam.legalRetentionUntil,
          dicom_metadata: exam.dicomMetadata || {}
        });
      } catch (err) {
        console.warn('R MAX: Falha ao salvar exame no cofre:', err);
      }
    }
  }, [session?.user]);

  const handleDeleteClinicalExam = React.useCallback(async (id: string) => {
    setClinicalExams(prev => prev.filter(e => e.id !== id));
    if (session?.user) {
      try {
        await supabase.from('clinical_exams_vault').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao deletar exame do cofre:', err);
      }
    }
  }, [session?.user]);

  const handleInviteAccountant = React.useCallback(async (data: { email: string; name?: string; firm?: string; crc?: string }) => {
    if (!session?.user?.id) {
      throw new Error('É necessário estar autenticado para convidar um contador para seu workspace.');
    }
    const newAccess: AccountantAccess = {
      id: crypto.randomUUID(),
      workspaceOwnerId: session.user.id,
      accountantEmail: data.email.trim().toLowerCase(),
      accountantName: data.name,
      accountingFirm: data.firm,
      crcNumber: data.crc,
      status: 'pending',
      invitedAt: new Date().toISOString(),
      permissions: {
        canExportTax: true,
        canViewReceipts: true,
        canViewMaterials: true,
        canViewClinicalData: false
      }
    };
    setAccountantAccesses(prev => [newAccess, ...prev]);

    if (session?.user) {
      try {
        await supabase.from('accountant_access').upsert({
          id: newAccess.id,
          workspace_owner_id: session.user.id,
          accountant_email: newAccess.accountantEmail,
          accountant_name: newAccess.accountantName || null,
          accounting_firm: newAccess.accountingFirm || null,
          crc_number: newAccess.crcNumber || null,
          status: 'pending',
          invited_at: newAccess.invitedAt,
          permissions: newAccess.permissions
        }, { onConflict: 'workspace_owner_id,accountant_email' });
      } catch (err) {
        console.warn('R MAX: Falha ao convidar contador:', err);
      }
    }
  }, [session?.user]);

  const handleRevokeAccountantAccess = React.useCallback(async (id: string) => {
    setAccountantAccesses(prev => prev.filter(a => a.id !== id));
    if (session?.user) {
      try {
        await supabase.from('accountant_access').delete().eq('id', id);
      } catch (err) {
        console.warn('R MAX: Falha ao revogar acesso contábil:', err);
      }
    }
  }, [session?.user]);

  return {
    isLoadingUserData,
    hasLoadedAllData,
    user,
    config,
    quotes,
    materials,
    recipes,
    services,
    clients,
    suppliers,
    workOrders,
    receipts,
    appointments,
    staffMembers,
    subscriptionClubs,
    storefrontProfile,
    inventoryBatches,
    stockMovements,
    clinicalPatients,
    clinicalConsultations,
    clinicalVaccinations,
    clinicalExams,
    accountantAccesses,
    isFirstVisit,
    setIsFirstVisit,
    loadUserData,
    handleOnboardingComplete,
    handleResetNicheData,
    handleProfileUpdate,
    handleSaveQuote,
    handleSaveRecipe,
    handleDeleteRecipe,
    handleSaveService,
    handleDeleteService,
    handleSaveMaterials,
    handleUpdateQuoteStatus,
    handleSaveClient,
    handleDeleteClient,
    handleSaveSupplier,
    handleDeleteSupplier,
    handleSaveWorkOrder,
    handleDeleteWorkOrder,
    handleSaveReceipt,
    handleDeleteReceipt,
    handleSaveAppointment,
    handleDeleteAppointment,
    handleUpdateAppointmentStatus,
    refreshAppointments,
    handleSaveSignature,
    handleSaveStaffMember,
    handleDeleteStaffMember,
    handleSaveStorefrontProfile,
    handleSaveSubscriptionClub,
    handleDeleteSubscriptionClub,
    handleSaveInventoryBatch,
    handleDeleteInventoryBatch,
    handleSaveStockMovement,
    handleSaveClinicalPatient,
    handleDeleteClinicalPatient,
    handleSaveClinicalConsultation,
    handleSaveClinicalVaccination,
    handleDeleteClinicalVaccination,
    handleSaveClinicalExam,
    handleDeleteClinicalExam,
    handleInviteAccountant,
    handleRevokeAccountantAccess,
    syncConfigToSupabase,
    setUser,
    setConfig,
    setQuotes,
    setMaterials,
    setRecipes,
    setServices,
    setClients,
    setSuppliers,
    setWorkOrders,
    setReceipts,
    setAppointments,
    setStaffMembers,
    setSubscriptionClubs,
    setStorefrontProfile,
    setInventoryBatches,
    setStockMovements,
    setClinicalPatients,
    setClinicalConsultations,
    setClinicalVaccinations,
    setClinicalExams,
    setAccountantAccesses,
    lastLoadedId
  };
}
